import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  ChevronRight,
  Clock,
  Loader2,
} from "lucide-react";
import {
  Link,
  Navigate,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  Button,
} from "@/components/ui/Button";
import {
  useAuth,
} from "@/components/AuthProvider";
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
} from "@/components/ui/Card";
import {
  CardTitleWithHelp,
  HoverHintWithGlyph,
} from "@/components/ui/title-help";
import {
  Input,
  Label,
} from "@/components/ui/Input";
import {
  cn,
  formatTimestamp,
} from "@/lib/utils";
import {
  invalidateClampQcLaborSessionQueries,
} from "@/lib/hubLaborActiveSession";
import {
  laborRemotePersistenceDisabled,
} from "@/lib/labor/laborEnv";
import {
  useLaborSession,
} from "@/lib/labor/useLaborSession";
import {
  ApiError,
} from "@/lib/api";
import {
  useHubToast,
} from "@/components/ui/HubToast";
import { ZPL_PRINTER_STORAGE_KEYS } from "@/lib/zplPrinter";
import { useZplPrintLocation } from "@/lib/zplPrintLocation";
import {
  playPairFailureSound,
} from "@/lib/floorStationPairAudio";
import {
  FLOOR_PAGE_SCAN_TARGET_PROPS,
  useFloorPageScanCapture,
} from "@/lib/floorPageScanCapture";
import {
  useHubModuleWrite,
} from "@/lib/useHubModuleWrite";
import {
  useIntegratorMe,
} from "@/lib/me-query";
import {
  PrePackCartonOperatorStack,
} from "@/pages/prepack/PrePackCartonOperatorStack";
import {
  OpenIncidentForm,
} from "@/components/qc/OpenIncidentForm";
import {
  InspectionTable,
  formatInspectionTimestamp,
  type InspectionTableRow,
} from "./clampQcInspectionTable";
import {
  CLAMP_QC_INDUCTANCE_LIMITS,
  CLAMP_QC_UNITS_PER_CARTON_LARGE,
  CLAMP_QC_UNITS_PER_CARTON_SMALL,
  clampInspectionSizeFromSku,
  clampInspectionUnitsPerCartonForSku,
  clampQcIsLocalSandboxSlug,
  type ClampQcWorkOrderBundle,
} from "./clampQcMockData";
import {
  CLAMPQC_HUB_WORK_ORDERS_QUERY_KEY,
  fetchClampQcHubWorkOrderBundle,
  fetchClampQcMostRecentSerial,
  type ClampQcHubWorkOrderListItem,
} from "./clampQcBuildOrdersApi";
import { ClampQcFinishToStockCard } from "./ClampQcFinishToStockCard";
import {
  clampQcSerialYmBatchLabel,
  clampQcSerialYmPrefixesDiffer,
} from "./clampQcSerialYmPrefix";
import {
  CLAMP_QC_SERIAL_ANCHOR_RANGE,
  clampQcSerialIsOutsideAnchorRange,
  parseClampQcSerialAnchorNumber,
} from "./clampQcSerialAnchor";
import {
  applyClampQcInspectionToCartons,
  applyClampQcRescanToCartons,
  clampQcCartonsForSerialInspection,
  clampQcShouldApplyInspectionToCartonCounts,
  parseClampQcCartonCountDraft,
} from "./clampQcCartonTrackers";
import {
  ClampQcCartonTrackersCard,
} from "./ClampQcCartonTrackersCard";
import {
  ClampQcHeaderPrinterSelect,
} from "./ClampQcInventoryBoxPrinterBar";
import {
  clampQcInventoryBoxPassedClampsQueryKey,
  clampQcOpenInventoryBoxQueryKey,
} from "./clampQcCartonProgressDisplay";
import {
  filterClampQcSessionFailedRows,
  mergeClampQcCartonTableRows,
  type ClampQcCartonSessionRow,
} from "./clampQcCartonSessionRows";
import { ClampQcRecentFailsCard } from "./ClampQcRecentFailsCard";
import {
  fetchClampQcInventoryBox,
  fetchClampQcInventoryBoxPassedClamps,
  fetchClampQcOpenBoxForOperator,
  openClampQcInventoryBoxAndPrint,
  reprintClampQcInventoryBoxLabel,
  removeClampFromInventoryBox,
  sealClampQcInventoryBox,
  type ClampQcInventoryBoxSnapshot,
} from "./clampQcInventoryBoxApi";
import {
  inventoryLabelSkuFromClampQcJobSku,
} from "@integrator-src/hub/clampqc-inventory-label-sku";
import { validateClampQcInspectionSerialFormat } from "@integrator-src/hub/clampqc-inspection-serial-format";
import { parseInventoryBoxIdFromScan } from "@integrator-src/api/inventory-box/inventory-box-scan";
import {
  postClampQcInspectionSlotReserve,
} from "./clampQcInspectionSlotApi";
import {
  postClampQcInspectionRecord,
} from "./clampQcInspectionRecordApi";
import {
  fetchClampQcInspectionExists,
} from "./clampQcInspectionExistsApi";
import {
  armClampQcFloorKeyboardCooldown,
  CLAMP_QC_FLOOR_KEYBOARD_COOLDOWN_MS,
  endClampQcSerialSubmit,
  isClampQcFloorKeyboardBlocked,
  shouldIgnoreClampQcFloorSerialScan,
  tryBeginClampQcSerialSubmit,
} from "./clampQcSerialSubmitGate";
import {
  playClampQcInspectionCompleteSound,
  playClampQcInspectionSubmitErrorSound,
} from "./clampQcInspectionCompleteAudio";
import { clampQcFailDispositionToastForInspectionDraft } from "./clampQcFailDisposition";
import {
  clampQcDuplicateSerialIncidentIssue,
  clampQcDuplicateSerialIncidentNotes,
  clampQcDuplicateSerialOnOpenJob,
  type ClampQcExistingInspectionDetails,
} from "./clampQcDuplicateSerial";
import {
  clampQcFailReasonLabelsFromSessionRow,
  clampQcFindSessionEntryBySerialKey,
  clampQcFormatMeasurementPrefill,
  clampQcSessionRescanAllowsModalSubmit,
  clampQcSessionRescanCartonSummary,
  type ClampQcSessionRescanConfirmation,
} from "./clampQcSessionRescan";
import {
  CLAMPQC_HUB_SETTINGS_QUERY_KEY,
  CLAMPQC_PHYSICAL_FAILURE_REASONS_QUERY_KEY,
  SANDBOX_CLAMP_QC_HUB_SETTINGS,
  appendClampQcPhysicalFailReason,
  clampQcDragBandForSku,
  clampQcDragFromUnloadedLoadedLines,
  clampQcDragLadderFromSettings,
  clampQcInductanceEntryBlocksInspectionSubmit,
  clampQcInductanceReadingIsOutOfBand,
  clampQcInductanceReadingHasInvalidNumeric,
  fetchClampQcHubSettings,
  fetchClampQcPhysicalFailureReasons,
  sortClampQcPhysicalFailReasonLabels,
  clampQcUnloadedLineFailsAcceptance,
  clampQcUnloadedLineAcceptanceRangeForSku,
} from "./clampQcSettingsApi";
import {
  lineHarvestPerformedForHubPersist,
} from "@integrator-src/hub/clamp-quality-clamp-map";
import {
  validateClampQcInspectionLineReadingsForUiSubmit,
  clampQcInspectionLineHarvestRequiredForUiSubmit,
} from "@integrator-src/hub/clampqc-inspection-line-readings";
import { clampQcMechanicalFailureSkipsOtherTests } from "@integrator-src/config/clampqc-fail-disposition";
import {
  clampQcWorkOrderQuantityCapExceeded,
  CLAMP_QC_WORK_ORDER_QUANTITY_CAP_ERROR,
} from "@integrator-src/hub/clampqc-work-order-quantity-cap";
import {
  CLAMPQC_LABOR_OPEN_FLASH_STORAGE_KEY,
  clampQcLaborPageKeyForJobSlug,
  formatLaborLocationLabel,
} from "./clampQcLaborApi";
import {
  clampQcActiveJobOperatorsQueryKey,
  CLAMP_QC_JOB_PRESENCE_INTERVAL_MS,
  fetchClampQcActiveJobOperators,
  filterClampQcOperatorsForJobSlug,
} from "./clampQcLaborPresenceApi";
import {
  formatManufacturingLaborDuration,
} from "./clampManufacturingLaborFormat";
import type {
  ClampQcLaborInterval,
  LaborBannerState,
} from "./clampQcPageSessionTypes";
import {
  PRIMARY_TABS,
  computedInspectionResult,
  draftToRow,
  emptyDraft,
  failReasonKeypadDigitCharacter,
  failReasonKeypadSingleKeySlot,
  failReasonKeypadStarArmKey,
  failReasonsKeypadHelpText,
  focusElementNextTick,
  hubOperatorAttribution,
  inductanceSectionHelpText,
  inspectionModalFormIncomplete,
  inspectionModalHasExplicitFailCriteria,
  inspectionSerialToStableKey,
  lineHarvestSectionHelpText,
  measurementInputKeyDownWithEnterFocusNext,
  physicalInspectionModalDigitTargetIgnoresFailKeypad,
  sanitizeDecimalMeasurementInput,
  parseOptionalFloat,
} from "./clampQcPageInspectionDraft";
import type {
  ClampQcInductanceEvaluationOptions,
  ClampSizeTab,
  DraftInspection,
  PrimaryTab,
} from "./clampQcPageInspectionDraft";

export function ClampQcPage() {
  const { slug } = useParams<{ slug: string }>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showToast } = useHubToast();
  const hubClampQcWritesAllowed = useHubModuleWrite("clampqc");
  const { session: authSession } = useAuth();
  const meQuery = useIntegratorMe();

  const bumpLaborToOverviewWithFlash = useCallback((message: string) => {
    try {
      sessionStorage.setItem(CLAMPQC_LABOR_OPEN_FLASH_STORAGE_KEY, message);
    } catch {
      /* private mode or storage quota */
    }
    navigate("/clamp-qc/inspection", { replace: true });
  }, [navigate]);
  const laborPage = useMemo(
    () => (slug != null && slug.trim() !== "" ? clampQcLaborPageKeyForJobSlug(slug) : ""),
    [slug],
  );
  const operatorAttribution = useMemo(
    () => hubOperatorAttribution(meQuery.data, authSession?.user?.email ?? ""),
    [meQuery.data, authSession?.user?.email],
  );
  const allowNonNumericInspectionSerial = import.meta.env.DEV;

  const remoteBundleQuery = useQuery({
    queryKey: ["clamp-qc", "work-order", slug],
    queryFn: () => fetchClampQcHubWorkOrderBundle(slug!),
    enabled: Boolean(slug?.trim()),
    staleTime: 30_000,
  });

  const bundle = remoteBundleQuery.data ?? null;

  const laborPageReady = remoteBundleQuery.isSuccess && laborPage.trim() !== "";
  const labor = useLaborSession({
    page: laborPage,
    domain: "clampqc",
    moduleGate: "clampqc",
    devPersistenceDisabled: () => laborRemotePersistenceDisabled("clampqc"),
    workLabel: "Clamp QC",
    autoStart: laborPageReady,
    requirePersistedClockIn: true,
    onIdleTimeout: () => {
      playPairFailureSound();
      bumpLaborToOverviewWithFlash(
        "Clocked out due to inactivity. Open the job again to keep working.",
      );
    },
    onPersistedSessionLost: () => {
      bumpLaborToOverviewWithFlash(
        "Lost contact with the open labor session on the server. Open the job again from the list when the network is stable.",
      );
    },
    onSessionEndedRemotely: () => {
      playPairFailureSound();
      bumpLaborToOverviewWithFlash(
        "Clocked out from another tab or the session was closed on the server. Open the job again from the list when you want to continue.",
      );
    },
    onStopped: () => {
      bumpLaborToOverviewWithFlash("Clocked out. Open the job again from the list when you want to continue.");
    },
  });

  const viewerUserId = authSession?.user?.id ?? null;
  const viewerKnown = viewerUserId !== null && viewerUserId !== "";

  const jobOperatorsQuery = useQuery({
    queryKey: clampQcActiveJobOperatorsQueryKey,
    queryFn: fetchClampQcActiveJobOperators,
    enabled: labor.gateReady && slug != null && slug.trim() !== "",
    staleTime: 10_000,
    refetchInterval: CLAMP_QC_JOB_PRESENCE_INTERVAL_MS,
  });

  const jobOperators = useMemo(
    () => filterClampQcOperatorsForJobSlug(jobOperatorsQuery.data ?? [], slug ?? ""),
    [jobOperatorsQuery.data, slug],
  );

  const showOtherOperatorsOnJob =
    jobOperators.length > 0 &&
    (!viewerKnown || jobOperators.some((operator) => operator.userId !== viewerUserId));

  const serialInputRef = useRef<HTMLInputElement>(null);
  /** Synchronous guard: a second scan+Enter while preflight is in flight is ignored (barcode wedge timing). */
  const clampQcSerialSubmitInFlightRef = useRef(false);
  /** Block wedge keystrokes briefly after serial submit or inspection save so a double-scan cannot hit the modal keypad. */
  const clampQcFloorKeyboardBlockedUntilRef = useRef(0);
  const clampQcFloorKeyboardCooldownTimerRef = useRef<number | null>(null);
  const [floorKeyboardBlocked, setFloorKeyboardBlocked] = useState(false);
  const armClampQcFloorKeyboardCooldownOnPage = useCallback(() => {
    armClampQcFloorKeyboardCooldown(clampQcFloorKeyboardBlockedUntilRef);
    setFloorKeyboardBlocked(true);
    if (clampQcFloorKeyboardCooldownTimerRef.current != null) {
      window.clearTimeout(clampQcFloorKeyboardCooldownTimerRef.current);
    }
    clampQcFloorKeyboardCooldownTimerRef.current = window.setTimeout(() => {
      clampQcFloorKeyboardCooldownTimerRef.current = null;
      setFloorKeyboardBlocked(isClampQcFloorKeyboardBlocked(clampQcFloorKeyboardBlockedUntilRef));
    }, CLAMP_QC_FLOOR_KEYBOARD_COOLDOWN_MS);
  }, []);
  const openInspectionFromScannedSerialRef = useRef<(serial: string) => void>(() => {});
  const inspectionModalFirstFieldRef = useRef<HTMLInputElement>(null);
  const inspectionModalInductanceFieldRef = useRef<HTMLInputElement>(null);
  const inspectionModalUnloadedFieldRef = useRef<HTMLInputElement>(null);
  const inspectionModalLoadedFieldRef = useRef<HTMLInputElement>(null);
  const inspectionModalLoadedWithTesterFieldRef = useRef<HTMLInputElement>(null);
  const inspectionModalLineHarvestChoiceGroupRef = useRef<HTMLDivElement>(null);
  const inspectionModalSubmitButtonRef = useRef<HTMLButtonElement>(null);
  const sortedPhysicalFailReasonsKeypadRef = useRef<readonly string[]>([]);
  const failKeypadStarSequenceRef = useRef<{ active: boolean; digits: string }>({ active: false, digits: "" });
  const dismissInspectionModalRef = useRef<() => void>(() => {});
  const lineHarvestBandPrevRef = useRef<string>("");
  const inspectionSlotReserveGenerationRef = useRef(0);
  /**
   * After `inspection-exists` returns false before opening the modal, persist is assumed to INSERT a clamp row (+1 hub
   * complete). After overwrite confirmation path, INSERT is skipped (UPDATE only)—optimistic tally must not bump.
   */
  const treatNextPersistAsNewClampRowRef = useRef(true);
  /** Set when the operator confirms in-session rescan; allows modal submit to replace the session row. */
  const sessionRescanArmedSerialKeyRef = useRef<number | null>(null);
  /** Last serial text opened for inspection in this browser session on the open GBO (not the field draft). */
  const lastScannedSerialTextForJobRef = useRef<string | null>(null);
  /** Latest persisted serial on this GBO from the hub (seeded when the job page loads). */
  const serverMostRecentSerialRef = useRef<string | null>(null);

  const [primaryTab, setPrimaryTab] = useState<PrimaryTab>("inspection");
  const [draft, setDraft] = useState<DraftInspection>(() => emptyDraft());
  const [sessionEntries, setSessionEntries] = useState<ClampQcCartonSessionRow[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  /**
   * Units remaining in the open incoming (pre-QC) carton. Starts at carton size and counts down on every inspection.
   * `null` until carton size for this job is known.
   */
  const [preQcRemaining, setPreQcRemaining] = useState<number | null>(null);
  const [preQcCountDraft, setPreQcCountDraft] = useState("0");
  const [activeInventoryBox, setActiveInventoryBox] = useState<ClampQcInventoryBoxSnapshot | null>(null);
  const [inventoryBoxSessionStarted, setInventoryBoxSessionStarted] = useState(false);
  const inventoryBoxHydratedBuildOrderIdRef = useRef<number | null>(null);
  const manualPreQcFieldDirtyRef = useRef(false);
  const preQcInitializedForSlugRef = useRef<string | null>(null);
  /** First numeric serial scanned on the current pre-QC carton. */
  const [serialAnchor, setSerialAnchor] = useState<number | null>(null);
  const serialAnchorRef = useRef<number | null>(null);
  const [serialAnchorConfirmation, setSerialAnchorConfirmation] = useState<null | {
    currentSerial: string;
    serialNumber: number;
    previousAnchor: number;
  }>(null);

  const updateSerialAnchor = (next: number | null): void => {
    serialAnchorRef.current = next;
    setSerialAnchor(next);
  };

  const [laborIntervals, setLaborIntervals] = useState<ClampQcLaborInterval[]>([]);
  const [lastLaborSummary, setLastLaborSummary] = useState<{ netSeconds: number; idleTimeout: boolean } | null>(null);
  const [laborScanBanner, setLaborScanBanner] = useState<LaborBannerState | null>(null);
  const [inspectionPersistBanner, setInspectionPersistBanner] = useState<LaborBannerState | null>(null);
  const [inspectionSaving, setInspectionSaving] = useState(false);
  const [inspectionDetailModalOpen, setInspectionDetailModalOpen] = useState(false);
  const [inspectSerialPreflightBusy, setInspectSerialPreflightBusy] = useState(false);
  const [serialOverwriteConfirmation, setSerialOverwriteConfirmation] = useState<ClampQcExistingInspectionDetails | null>(
    null,
  );
  const [sessionRescanConfirmation, setSessionRescanConfirmation] = useState<ClampQcSessionRescanConfirmation | null>(
    null,
  );
  const [duplicateSerialIncidentOpen, setDuplicateSerialIncidentOpen] = useState(false);
  const [serialYmMismatchConfirmation, setSerialYmMismatchConfirmation] = useState<null | {
    previousSerial: string;
    currentSerial: string;
  }>(null);
  useFloorPageScanCapture({
    enabled:
      primaryTab === "inspection" &&
      !inspectionDetailModalOpen &&
      !inspectSerialPreflightBusy &&
      !floorKeyboardBlocked &&
      !duplicateSerialIncidentOpen &&
      serialYmMismatchConfirmation === null &&
      serialAnchorConfirmation === null,
    inputRef: serialInputRef,
    onScan: (serial) => openInspectionFromScannedSerialRef.current(serial),
  });

  useEffect(() => {
    if (!floorKeyboardBlocked) {
      return;
    }
    const blockStrayKeyboard = (event: KeyboardEvent): void => {
      if (!isClampQcFloorKeyboardBlocked(clampQcFloorKeyboardBlockedUntilRef)) {
        return;
      }
      if (event.ctrlKey || event.metaKey || event.altKey) {
        return;
      }
      if (event.key === "Enter" || event.key === "Tab" || event.key.length === 1) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    document.addEventListener("keydown", blockStrayKeyboard, true);
    return () => {
      document.removeEventListener("keydown", blockStrayKeyboard, true);
    };
  }, [floorKeyboardBlocked]);
  /**
   * After the server (or mock job) reserves a QC ordinal for this serial: whether inductance must be recorded.
   * `null` means reservation is still in flight—Record stays disabled.
   */
  const [inspectionModalInductanceMeasurementRequired, setInspectionModalInductanceMeasurementRequired] = useState<
    boolean | null
  >(null);
  /** Filled after inspection-slot reserve succeeds so operators can confirm which hub percent applied to this serial. */
  const [_inspectionSlotSamplingMeta, setInspectionSlotSamplingMeta] = useState<{
    slotOrdinal: number;
    plannedQuantity: number;
    inductanceTestingTargetCount: number;
    inductanceTestingPercentPerWorkOrder: number;
  } | null>(null);
  /** Remembered on this job session for the next unit’s modal (unloaded line). */
  const [sessionUnloadedLine, setSessionUnloadedLine] = useState("");
  /** Modal-only strings; committed to `draft` on blur so drag and band messaging do not move while typing. */
  const [modalInductanceInput, setModalInductanceInput] = useState("");
  const [modalUnloadedInput, setModalUnloadedInput] = useState("");
  const [modalLoadedInput, setModalLoadedInput] = useState("");
  const [modalLoadedWithTesterInput, setModalLoadedWithTesterInput] = useState("");
  const [newPhysicalFailReasonDraft, setNewPhysicalFailReasonDraft] = useState("");
  const [appendFailReasonMessage, setAppendFailReasonMessage] = useState<string | null>(null);
  const [removeFromCartonModalOpen, setRemoveFromCartonModalOpen] = useState(false);
  const [removeFromCartonSerialDraft, setRemoveFromCartonSerialDraft] = useState("");
  const [removeFromCartonError, setRemoveFromCartonError] = useState<string | null>(null);

  const clampLaborStartedAtRef = useRef<string | null>(null);
  useEffect(() => {
    if (labor.session?.started_at) {
      clampLaborStartedAtRef.current = labor.session.started_at;
    }
  }, [labor.session?.started_at]);

  const laborCompletedSigRef = useRef<string | null>(null);
  useEffect(() => {
    if (labor.lastSummary === null) {
      laborCompletedSigRef.current = null;
      return;
    }
    if (bundle === null) return;
    const startedIso = clampLaborStartedAtRef.current;
    if (startedIso === null) return;
    const summary = labor.lastSummary;
    const sig = `${startedIso}:${summary.netSeconds}:${summary.idleTimeout}:${summary.deviceCount ?? ""}`;
    if (laborCompletedSigRef.current === sig) return;
    laborCompletedSigRef.current = sig;
    const endedAtIso = new Date().toISOString();
    const initials = operatorAttribution;
    const workOrderContext = bundle.context;
    setLaborIntervals((previous) => [
      {
        id: crypto.randomUUID(),
        workOrder: workOrderContext.workOrder,
        buildOrderId: bundle.buildOrderId,
        initials,
        netSeconds: summary.netSeconds,
        idleTimeout: summary.idleTimeout,
        startedAtIso: startedIso,
        endedAtIso,
      },
      ...previous,
    ]);
    setLastLaborSummary({ netSeconds: summary.netSeconds, idleTimeout: summary.idleTimeout });
    invalidateClampQcLaborSessionQueries(queryClient);
  }, [labor.lastSummary, bundle, operatorAttribution, queryClient]);

  useEffect(() => {
    if (labor.sessionId !== null) {
      invalidateClampQcLaborSessionQueries(queryClient);
    }
  }, [labor.sessionId, queryClient]);

  const laborGateConflictBumpRef = useRef(false);
  useEffect(() => {
    if (!remoteBundleQuery.isSuccess || laborPage.trim() === "") return;
    if (labor.loading) return;
    if (labor.activeOnOtherPage) {
      if (!laborGateConflictBumpRef.current) {
        laborGateConflictBumpRef.current = true;
        bumpLaborToOverviewWithFlash(
          `You are already clocked in on ${formatLaborLocationLabel(labor.activeOnOtherPage)}. Close that session, then open this job again.`,
        );
      }
      return;
    }
    laborGateConflictBumpRef.current = false;
  }, [
    remoteBundleQuery.isSuccess,
    laborPage,
    labor.loading,
    labor.activeOnOtherPage,
    bumpLaborToOverviewWithFlash,
  ]);

  const laborGateFailureBumpRef = useRef(false);
  useEffect(() => {
    if (!remoteBundleQuery.isSuccess || laborPage.trim() === "") return;
    if (labor.loading || labor.gateReady) return;
    if (labor.activeOnOtherPage) return;
    if (!labor.banner?.message) return;
    if (laborGateFailureBumpRef.current) return;
    laborGateFailureBumpRef.current = true;
    bumpLaborToOverviewWithFlash(labor.banner.message);
  }, [
    remoteBundleQuery.isSuccess,
    laborPage,
    labor.loading,
    labor.gateReady,
    labor.activeOnOtherPage,
    labor.banner?.message,
    bumpLaborToOverviewWithFlash,
  ]);

  const laborPersistEligible =
    hubClampQcWritesAllowed &&
    labor.sessionId !== null &&
    labor.sessionId > 0 &&
    !laborRemotePersistenceDisabled("clampqc");

  const inventoryBoxFlowEnabled =
    !clampQcIsLocalSandboxSlug(slug) && hubClampQcWritesAllowed;

  const sessionFailedRows = useMemo(
    () => filterClampQcSessionFailedRows(sessionEntries),
    [sessionEntries],
  );

  const openBoxHex = activeInventoryBox?.hex ?? "";

  const cartonPassedRowsQuery = useQuery({
    queryKey: clampQcInventoryBoxPassedClampsQueryKey(openBoxHex, bundle?.buildOrderId ?? 0),
    queryFn: async () => {
      if (activeInventoryBox == null || bundle == null) {
        return [];
      }
      const response = await fetchClampQcInventoryBoxPassedClamps(
        activeInventoryBox.hex,
        bundle.buildOrderId,
      );
      return response.rows;
    },
    enabled:
      inventoryBoxFlowEnabled &&
      activeInventoryBox != null &&
      !activeInventoryBox.sealed &&
      bundle != null,
    staleTime: 3_000,
  });

  const cartonRowsForTable = useMemo(() => {
    if (activeInventoryBox == null || activeInventoryBox.sealed) {
      return [];
    }
    return mergeClampQcCartonTableRows({
      serverRows: cartonPassedRowsQuery.data ?? [],
      sessionRows: sessionEntries,
      openBoxHex: activeInventoryBox.hex,
    });
  }, [activeInventoryBox, sessionEntries, cartonPassedRowsQuery.data]);

  const unitsInOpenCarton = activeInventoryBox?.filledCount ?? cartonRowsForTable.length;

  const inspectionClampFromSku = useMemo(() => clampInspectionSizeFromSku(bundle?.sku ?? ""), [bundle?.sku]);

  const clampQcHubSettingsQuery = useQuery({
    queryKey: CLAMPQC_HUB_SETTINGS_QUERY_KEY,
    queryFn: fetchClampQcHubSettings,
    staleTime: 30_000,
  });

  const physicalFailureReasonsQuery = useQuery({
    queryKey: CLAMPQC_PHYSICAL_FAILURE_REASONS_QUERY_KEY,
    queryFn: fetchClampQcPhysicalFailureReasons,
    staleTime: 120_000,
  });

  const hubSettings =
    clampQcHubSettingsQuery.data ??
    (clampQcIsLocalSandboxSlug(slug) ? SANDBOX_CLAMP_QC_HUB_SETTINGS : null);

  const appendPhysicalFailReasonMutation = useMutation({
    mutationFn: ({ label }: { label: string }) => appendClampQcPhysicalFailReason(label),
    onSuccess: (response, variables) => {
      void queryClient.invalidateQueries({ queryKey: CLAMPQC_PHYSICAL_FAILURE_REASONS_QUERY_KEY });
      const trimmed = variables.label.trim();
      const key = trimmed.toLowerCase();
      const canonical = response.reason.reason.trim();
      setNewPhysicalFailReasonDraft("");
      setAppendFailReasonMessage(null);
      setDraft((previous) => {
        const already = previous.failReasons.some((reason) => reason.toLowerCase() === key);
        return already
          ? previous
          : { ...previous, failReasons: [...previous.failReasons, canonical] };
      });
    },
    onError: (error) => {
      const message =
        error instanceof ApiError ? error.message : "Could not save the new fail reason.";
      setAppendFailReasonMessage(message);
    },
  });

  const openInventoryBoxQuery = useQuery({
    queryKey: clampQcOpenInventoryBoxQueryKey(bundle?.buildOrderId ?? 0),
    queryFn: async () => {
      if (bundle == null) {
        return null;
      }
      const response = await fetchClampQcOpenBoxForOperator(bundle.buildOrderId);
      return response.box;
    },
    enabled: inventoryBoxFlowEnabled && bundle != null,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const syncOpenInventoryBoxCache = useCallback(
    (buildOrderId: number, box: ClampQcInventoryBoxSnapshot | null): void => {
      queryClient.setQueryData(clampQcOpenInventoryBoxQueryKey(buildOrderId), box);
    },
    [queryClient],
  );

  const invalidateCartonPassedRows = useCallback(
    (hex: string, buildOrderId: number): void => {
      void queryClient.invalidateQueries({
        queryKey: clampQcInventoryBoxPassedClampsQueryKey(hex, buildOrderId),
      });
    },
    [queryClient],
  );

  useEffect(() => {
    if (bundle == null || !inventoryBoxFlowEnabled) {
      return;
    }
    if (openInventoryBoxQuery.data === undefined) {
      return;
    }
    const buildOrderId = bundle.buildOrderId;
    if (inventoryBoxHydratedBuildOrderIdRef.current === buildOrderId) {
      return;
    }
    inventoryBoxHydratedBuildOrderIdRef.current = buildOrderId;
    const openBox = openInventoryBoxQuery.data;
    setActiveInventoryBox(openBox);
    if (openBox != null) {
      setInventoryBoxSessionStarted(true);
    }
  }, [bundle, inventoryBoxFlowEnabled, openInventoryBoxQuery.data]);

  const {
    station,
    setStation,
    selectedPrinter: selectedZplPrinter,
    unavailableMessage, documents,
  } = useZplPrintLocation({
    storageKey: ZPL_PRINTER_STORAGE_KEYS.clampQc,
    documents: [{ id: "clamp", label: "Clamp", labelSize: "4x2", dpi: 300 }],
  });

  const openInventoryBoxMutation = useMutation({
    mutationFn: (input: { buildOrderId: number; capacity: number; printer: string }) =>
      openClampQcInventoryBoxAndPrint(input),
    onSuccess: (data) => {
      setActiveInventoryBox(data.box);
      setInventoryBoxSessionStarted(true);
      syncOpenInventoryBoxCache(data.box.buildOrderId, data.box);
      invalidateCartonPassedRows(data.box.hex, data.box.buildOrderId);
      showToast({
        tone: "success",
        message: `Box ${data.box.shortCode} label printed (${String(data.printedQuantity)} units capacity).`,
      });
    },
    onError: (error) => {
      showToast({
        tone: "error",
        message: error instanceof ApiError ? error.message : "Could not print the box label.",
      });
    },
  });

  const reprintInventoryBoxMutation = useMutation({
    mutationFn: (input: { hex: string; printer: string }) => reprintClampQcInventoryBoxLabel(input),
    onSuccess: () => {
      showToast({ tone: "success", message: "Box label reprint sent to printer." });
    },
    onError: (error) => {
      showToast({
        tone: "error",
        message: error instanceof ApiError ? error.message : "Could not reprint the box label.",
      });
    },
  });

  const sealInventoryBoxMutation = useMutation({
    mutationFn: (input: { hex: string; buildOrderId: number; printer: string }) =>
      sealClampQcInventoryBox(input),
    onSuccess: (data) => {
      setActiveInventoryBox(null);
      setInventoryBoxSessionStarted(true);
      syncOpenInventoryBoxCache(data.box.buildOrderId, null);
      showToast({
        tone: data.partialRelabel ? "warning" : "success",
        message: data.partialRelabel
          ? `Box sealed with ${String(data.box.filledCount)} units. Partial label reprinted.`
          : `Box sealed with ${String(data.box.filledCount)} units.`,
      });
    },
    onError: (error) => {
      showToast({
        tone: "error",
        message: error instanceof ApiError ? error.message : "Could not seal this box.",
      });
    },
  });

  const removeClampFromCartonMutation = useMutation({
    mutationFn: (input: { hex: string; buildOrderId: number; serialText: string }) =>
      removeClampFromInventoryBox(input),
    onSuccess: (data, variables) => {
      setActiveInventoryBox(data.box);
      syncOpenInventoryBoxCache(data.box.buildOrderId, data.box);
      invalidateCartonPassedRows(variables.hex, variables.buildOrderId);
      const serialKey = inspectionSerialToStableKey(variables.serialText.trim(), allowNonNumericInspectionSerial);
      if (serialKey != null) {
        setSessionEntries((previous) =>
          previous.map((entry) =>
            entry.serial === serialKey ? { ...entry, cartonInventoryBoxHex: null } : entry,
          ),
        );
      }
      setRemoveFromCartonModalOpen(false);
      setRemoveFromCartonSerialDraft("");
      setRemoveFromCartonError(null);
      showToast({
        tone: "success",
        message: `Removed serial ${variables.serialText.trim()} from box ${data.box.shortCode}. QC pass/fail is unchanged.`,
      });
    },
    onError: (error) => {
      setRemoveFromCartonError(error instanceof ApiError ? error.message : "Could not remove this serial from the box.");
    },
  });

  const inventoryBoxMutationBusy =
    openInventoryBoxMutation.isPending ||
    reprintInventoryBoxMutation.isPending ||
    sealInventoryBoxMutation.isPending ||
    removeClampFromCartonMutation.isPending;

  const cartonUnitsForSku = useMemo(() => {
    if (clampQcHubSettingsQuery.data !== undefined) {
      return {
        unitsPerCartonLarge: clampQcHubSettingsQuery.data.unitsPerCartonLarge,
        unitsPerCartonSmall: clampQcHubSettingsQuery.data.unitsPerCartonSmall,
      };
    }
    if (clampQcIsLocalSandboxSlug(slug)) {
      return {
        unitsPerCartonLarge: CLAMP_QC_UNITS_PER_CARTON_LARGE,
        unitsPerCartonSmall: CLAMP_QC_UNITS_PER_CARTON_SMALL,
      };
    }
    return null;
  }, [clampQcHubSettingsQuery.data, slug]);

  const packSizeDerivedFromClampLine = useMemo(() => {
    if (cartonUnitsForSku == null) {
      return null;
    }
    return clampInspectionUnitsPerCartonForSku(bundle?.sku ?? "", {
      unitsPerCartonLarge: cartonUnitsForSku.unitsPerCartonLarge,
      unitsPerCartonSmall: cartonUnitsForSku.unitsPerCartonSmall,
    });
  }, [bundle?.sku, cartonUnitsForSku]);

  useEffect(() => {
    const slugKey = slug?.trim() ?? "";
    if (slugKey === "" || packSizeDerivedFromClampLine == null || packSizeDerivedFromClampLine <= 0) {
      return;
    }
    if (preQcInitializedForSlugRef.current === slugKey) {
      return;
    }
    preQcInitializedForSlugRef.current = slugKey;
    manualPreQcFieldDirtyRef.current = false;
    setPreQcRemaining(packSizeDerivedFromClampLine);
    setPreQcCountDraft(String(packSizeDerivedFromClampLine));
  }, [packSizeDerivedFromClampLine, slug]);

  /** Session/reporting rows need large | small; unknown SKU defaults to large for this column alongside the banner warning. */
  const sessionClampSizeTag: ClampSizeTab = inspectionClampFromSku === "small" ? "small" : "large";

  const completedLaborSecondsTotal = useMemo(
    () => laborIntervals.reduce((sum, row) => sum + Math.max(0, row.netSeconds), 0),
    [laborIntervals],
  );

  useEffect(() => {
    setPreQcRemaining(null);
    setPreQcCountDraft("0");
    setActiveInventoryBox(null);
    setInventoryBoxSessionStarted(false);
    inventoryBoxHydratedBuildOrderIdRef.current = null;
    manualPreQcFieldDirtyRef.current = false;
    preQcInitializedForSlugRef.current = null;
    updateSerialAnchor(null);
    setSerialAnchorConfirmation(null);
    setSessionEntries([]);
    setFormError(null);
    setLaborIntervals([]);
    setLastLaborSummary(null);
    setLaborScanBanner(null);
    laborGateConflictBumpRef.current = false;
    laborGateFailureBumpRef.current = false;
    setInspectionDetailModalOpen(false);
    setSerialOverwriteConfirmation(null);
    setSessionRescanConfirmation(null);
    setSerialYmMismatchConfirmation(null);
    setInspectSerialPreflightBusy(false);
    setInspectionPersistBanner(null);
    setSessionUnloadedLine("");
    treatNextPersistAsNewClampRowRef.current = true;
    sessionRescanArmedSerialKeyRef.current = null;
    lastScannedSerialTextForJobRef.current = null;
    serverMostRecentSerialRef.current = null;
    const slugKey = slug?.trim();
    if (slugKey !== undefined && slugKey !== "" && !clampQcIsLocalSandboxSlug(slugKey)) {
      void fetchClampQcMostRecentSerial(slugKey)
        .then((payload) => {
          const serial = payload.serial?.trim() ?? "";
          serverMostRecentSerialRef.current = serial !== "" ? serial : null;
        })
        .catch(() => {
          serverMostRecentSerialRef.current = null;
        });
    }
  }, [slug]);

  useEffect(() => {
    if (manualPreQcFieldDirtyRef.current || preQcRemaining === null) {
      return;
    }
    setPreQcCountDraft(String(preQcRemaining));
  }, [preQcRemaining]);

  useEffect(() => {
    if (primaryTab !== "inspection" || inspectionDetailModalOpen) {
      return;
    }
    const id = window.requestAnimationFrame(() => {
      serialInputRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(id);
  }, [bundle?.slug, primaryTab, sessionEntries.length, inspectionDetailModalOpen]);

  useEffect(() => {
    if (!inspectionDetailModalOpen || inspectionModalInductanceMeasurementRequired === null) {
      return;
    }
    const id = window.requestAnimationFrame(() => {
      inspectionModalFirstFieldRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(id);
  }, [inspectionDetailModalOpen, inspectionModalInductanceMeasurementRequired]);

  useEffect(() => {
    if (!inspectionDetailModalOpen) {
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [inspectionDetailModalOpen]);

  useEffect(() => {
    if (!inspectionDetailModalOpen) {
      failKeypadStarSequenceRef.current = { active: false, digits: "" };
      return;
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (isClampQcFloorKeyboardBlocked(clampQcFloorKeyboardBlockedUntilRef)) {
        if (event.key === "Enter" || event.key === "Tab" || event.key.length === 1) {
          event.preventDefault();
          event.stopPropagation();
        }
        return;
      }
      const starRef = failKeypadStarSequenceRef;

      if (event.key === "Escape") {
        if (starRef.current.active) {
          starRef.current = { active: false, digits: "" };
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        dismissInspectionModalRef.current();
        return;
      }

      if (event.ctrlKey || event.metaKey || event.altKey || event.repeat) {
        return;
      }
      if (event.isComposing) {
        return;
      }
      if (physicalInspectionModalDigitTargetIgnoresFailKeypad(event.target)) {
        return;
      }
      if (
        event.target instanceof HTMLElement &&
        event.target.closest("[data-clamp-qc-line-harvest-keypad]")
      ) {
        return;
      }

      const list = sortedPhysicalFailReasonsKeypadRef.current;
      const listLength = list.length;

      if (starRef.current.active && listLength > 10) {
        const digitChar = failReasonKeypadDigitCharacter(event.key);
        if (digitChar !== null) {
          if (starRef.current.digits.length < 4) {
            starRef.current = { active: true, digits: starRef.current.digits + digitChar };
            event.preventDefault();
          }
          return;
        }
        if (event.key === "Backspace") {
          if (starRef.current.digits.length > 0) {
            starRef.current = {
              active: true,
              digits: starRef.current.digits.slice(0, -1),
            };
            event.preventDefault();
          }
          return;
        }
        if (event.key === "Enter") {
          const raw = starRef.current.digits.trim();
          if (raw === "") {
            starRef.current = { active: false, digits: "" };
            event.preventDefault();
            return;
          }
          const slot = Number.parseInt(raw, 10);
          if (
            !Number.isFinite(slot) ||
            slot < 1 ||
            slot > listLength ||
            String(slot) !== raw
          ) {
            starRef.current = { active: false, digits: "" };
            event.preventDefault();
            return;
          }
          starRef.current = { active: false, digits: "" };
          event.preventDefault();
          const index = slot - 1;
          const reason = list[index]!;
          setDraft((previous) => {
            const nextHas = previous.failReasons.includes(reason);
            return {
              ...previous,
              failReasons: nextHas
                ? previous.failReasons.filter((entry) => entry !== reason)
                : [...previous.failReasons, reason],
            };
          });
          return;
        }
        if (failReasonKeypadStarArmKey(event.key)) {
          starRef.current = { active: false, digits: "" };
          event.preventDefault();
          return;
        }
        return;
      }

      if (listLength > 10 && failReasonKeypadStarArmKey(event.key)) {
        starRef.current = { active: true, digits: "" };
        event.preventDefault();
        return;
      }

      const slot = failReasonKeypadSingleKeySlot(event.key);
      if (slot === null) {
        return;
      }
      const index = slot - 1;
      if (index < 0 || index >= listLength) {
        return;
      }
      event.preventDefault();
      const reason = list[index]!;
      setDraft((previous) => {
        const nextHas = previous.failReasons.includes(reason);
        return {
          ...previous,
          failReasons: nextHas
            ? previous.failReasons.filter((entry) => entry !== reason)
            : [...previous.failReasons, reason],
        };
      });
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [inspectionDetailModalOpen]);

  const modalSkuForHarvest = bundle?.sku ?? "";

  const sortedPhysicalFailReasons = useMemo(() => {
    const databaseLabels = physicalFailureReasonsQuery.data?.reasons.map((row) => row.reason) ?? [];
    return sortClampQcPhysicalFailReasonLabels(databaseLabels);
  }, [physicalFailureReasonsQuery.data]);
  sortedPhysicalFailReasonsKeypadRef.current = sortedPhysicalFailReasons;

  const modalLineDrag = useMemo(
    () => clampQcDragFromUnloadedLoadedLines(draft.unloadedLine, draft.loadedLineBeforeTester),
    [draft.unloadedLine, draft.loadedLineBeforeTester],
  );
  const modalDragLadder = useMemo(
    () => clampQcDragLadderFromSettings(modalSkuForHarvest, hubSettings),
    [modalSkuForHarvest, hubSettings],
  );
  const modalLineHarvestBand = useMemo(
    () => clampQcDragBandForSku(modalLineDrag, modalSkuForHarvest, hubSettings),
    [modalLineDrag, modalSkuForHarvest, hubSettings],
  );

  const inspectionModalCommittedPreview = useMemo(
    (): DraftInspection => ({
      ...draft,
      inductance: sanitizeDecimalMeasurementInput(modalInductanceInput),
      unloadedLine: sanitizeDecimalMeasurementInput(modalUnloadedInput),
      loadedLineBeforeTester: sanitizeDecimalMeasurementInput(modalLoadedInput),
      loadedLineWithTester: sanitizeDecimalMeasurementInput(modalLoadedWithTesterInput),
    }),
    [draft, modalInductanceInput, modalUnloadedInput, modalLoadedInput, modalLoadedWithTesterInput],
  );

  const inspectionModalMechanicalFailSkipsOtherTests = clampQcMechanicalFailureSkipsOtherTests(
    inspectionModalCommittedPreview.failReasons,
  );

  const inspectionEvaluationOptions = useMemo((): ClampQcInductanceEvaluationOptions => {
    return {
      inductanceSampled:
        inspectionModalInductanceMeasurementRequired === true && !inspectionModalMechanicalFailSkipsOtherTests,
    };
  }, [inspectionModalInductanceMeasurementRequired, inspectionModalMechanicalFailSkipsOtherTests]);

  const inspectionModalFormIncompleteFlag = useMemo(
    () =>
      (inspectionModalInductanceMeasurementRequired === null && !inspectionModalMechanicalFailSkipsOtherTests) ||
      inspectionModalFormIncomplete(
        inspectionModalCommittedPreview,
        modalSkuForHarvest,
        hubSettings,
        inspectionEvaluationOptions,
      ),
    [
      inspectionModalInductanceMeasurementRequired,
      inspectionModalMechanicalFailSkipsOtherTests,
      inspectionModalCommittedPreview,
      modalSkuForHarvest,
      hubSettings,
      inspectionEvaluationOptions,
    ],
  );

  const inspectionModalSubmitOutcomeResolved = useMemo((): "Passed" | "Failed" | null => {
    if (inspectionModalFormIncompleteFlag) {
      return null;
    }
    return computedInspectionResult(
      inspectionModalCommittedPreview,
      modalSkuForHarvest,
      hubSettings,
      inspectionEvaluationOptions,
    );
  }, [
    inspectionModalFormIncompleteFlag,
    inspectionModalCommittedPreview,
    modalSkuForHarvest,
    hubSettings,
    inspectionEvaluationOptions,
  ]);

  useEffect(() => {
    if (!inspectionDetailModalOpen) {
      lineHarvestBandPrevRef.current = "";
      return;
    }
    const band = modalLineHarvestBand;
    const previous = lineHarvestBandPrevRef.current;
    lineHarvestBandPrevRef.current = band;
    if (band === "gray" && previous !== "gray") {
      setDraft((previousDraft) => ({ ...previousDraft, lineHarvestFail: null }));
    }
    if (band !== "gray" && previous === "gray") {
      setModalLoadedWithTesterInput("");
      setDraft((previousDraft) => ({ ...previousDraft, loadedLineWithTester: "" }));
    }
  }, [inspectionDetailModalOpen, modalLineHarvestBand]);

  if (!slug || slug.trim() === "") {
    return <Navigate replace to="/clamp-qc/inspection" />;
  }

  if (remoteBundleQuery.isPending) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center gap-2 text-slate-600">
        <Loader2 aria-hidden className="h-5 w-5 animate-spin" />
        <span className="text-sm">Loading work order…</span>
      </div>
    );
  }

  if (remoteBundleQuery.isError) {
    const message =
      remoteBundleQuery.error instanceof Error ? remoteBundleQuery.error.message : "Could not load this work order.";
    return (
      <div className="space-y-4">
        <p className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">{message}</p>
        <p className="text-sm text-slate-600">
          <Link className="font-medium text-brand-700 hover:text-brand-800" to="/clamp-qc/inspection">
            Back to inspection list
          </Link>
        </p>
      </div>
    );
  }

  if (bundle === null) {
    return <Navigate replace to="/clamp-qc/inspection" />;
  }

  if (labor.loading || !labor.gateReady) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-2 text-slate-600">
        <Loader2 aria-hidden className="h-5 w-5 animate-spin" />
        <span className="text-sm font-medium text-slate-700">Clocking in…</span>
        <span className="max-w-sm text-center text-xs text-slate-500">
          Stay on this page until the labor session is ready. If clock-in fails, you will return to the inspection list
          with a message.
        </span>
      </div>
    );
  }

  const workOrderContext = bundle.context;
  const jobQuantity = workOrderContext.plannedQuantityFromWorkbook;
  const baselineComplete = bundle.quantityComplete;
  const sessionAdded = sessionEntries.length;
  const progressNumerator = baselineComplete + sessionAdded;
  const progressPct = jobQuantity <= 0 ? 0 : Math.min(100, Math.round((progressNumerator / jobQuantity) * 100));
  const remaining = Math.max(0, jobQuantity - baselineComplete);
  const workOrderQuantityCapReached = jobQuantity > 0 && baselineComplete >= jobQuantity;
  const clampQcSerialScanWouldExceedWorkOrderQuantity = (
    recordedForBuildOrderId: number | null | undefined,
  ): boolean =>
    clampQcWorkOrderQuantityCapExceeded({
      plannedQuantity: jobQuantity,
      currentCompleteCount: baselineComplete,
      serialAlreadyRecordedOnThisWorkOrder:
        recordedForBuildOrderId != null && recordedForBuildOrderId === bundle.buildOrderId,
    });
  const boxCapacity = packSizeDerivedFromClampLine;
  const inventoryLabelSku = inventoryLabelSkuFromClampQcJobSku(bundle?.sku ?? "");
  const preQcRemainingCount =
    preQcRemaining ?? (boxCapacity != null && boxCapacity > 0 ? boxCapacity : 0);
  const preQcCartonEmpty = boxCapacity != null && boxCapacity > 0 && preQcRemaining !== null && preQcRemaining <= 0;

  const applyManualPreQcUnits = (): void => {
    const parsed = parseClampQcCartonCountDraft(preQcCountDraft, preQcRemainingCount);
    manualPreQcFieldDirtyRef.current = false;
    setPreQcRemaining(parsed);
    setPreQcCountDraft(String(parsed));
    labor.touchActivity();
  };

  const startNextPreQcCarton = (): void => {
    if (boxCapacity == null || boxCapacity <= 0) {
      return;
    }
    manualPreQcFieldDirtyRef.current = false;
    setPreQcRemaining(boxCapacity);
    setPreQcCountDraft(String(boxCapacity));
    updateSerialAnchor(null);
    setSerialAnchorConfirmation(null);
    setFormError(null);
  };

  const resumeInventoryBoxFromScan = async (hex: string): Promise<void> => {
    try {
      const response = await fetchClampQcInventoryBox(hex);
      const box = response.box;
      if (box.buildOrderId !== bundle?.buildOrderId) {
        setFormError("This box belongs to a different work order.");
        return;
      }
      if (box.sealed) {
        setFormError("This box is already sealed. Print or scan a different open box.");
        return;
      }
      if (
        inventoryBoxFlowEnabled &&
        authSession?.user?.id != null &&
        box.ownerUserId != null &&
        box.ownerUserId !== authSession.user.id
      ) {
        setFormError("This box belongs to another operator. Scan your own open box.");
        return;
      }
      setActiveInventoryBox(box);
      setInventoryBoxSessionStarted(true);
      if (bundle != null) {
        syncOpenInventoryBoxCache(bundle.buildOrderId, box);
      }
      setFormError(null);
      showToast({ tone: "success", message: `Resumed box ${box.shortCode} (${String(box.filledCount)}/${String(box.capacity)}).` });
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : "Could not load this box label.");
    }
  };

  const requireOpenInventoryBoxForPass = (): boolean => {
    if (!inventoryBoxFlowEnabled) {
      return true;
    }
    if (activeInventoryBox == null || activeInventoryBox.sealed) {
      setFormError("Print or scan an open post-QC box label before recording passed units.");
      return false;
    }
    return true;
  };

  const openInventoryBoxForJob = (): void => {
    if (bundle == null || boxCapacity == null || boxCapacity <= 0) {
      return;
    }
    if (selectedZplPrinter.trim() === "") {
      showToast({ tone: "error", message: "Select a 4x2 label printer first." });
      return;
    }
    openInventoryBoxMutation.mutate({
      buildOrderId: bundle.buildOrderId,
      capacity: boxCapacity,
      printer: selectedZplPrinter,
    });
  };

  const reprintActiveInventoryBoxLabel = (): void => {
    if (activeInventoryBox == null) {
      return;
    }
    if (selectedZplPrinter.trim() === "") {
      showToast({ tone: "error", message: "Select a 4x2 label printer first." });
      return;
    }
    reprintInventoryBoxMutation.mutate({ hex: activeInventoryBox.hex, printer: selectedZplPrinter });
  };

  const sealActiveInventoryBox = (): void => {
    if (bundle == null || activeInventoryBox == null) {
      return;
    }
    if (selectedZplPrinter.trim() === "" && activeInventoryBox.filledCount < activeInventoryBox.capacity) {
      showToast({ tone: "error", message: "Select a printer for the partial box label reprint." });
      return;
    }
    sealInventoryBoxMutation.mutate({
      hex: activeInventoryBox.hex,
      buildOrderId: bundle.buildOrderId,
      printer: selectedZplPrinter,
    });
  };

  const startNextInventoryBoxAfterSeal = (): void => {
    openInventoryBoxForJob();
  };

  const startNextCartonsForNewSerialIfNeeded = (trimmedSerial: string): void => {
    if (boxCapacity == null || boxCapacity <= 0) {
      return;
    }
    const rolled = clampQcCartonsForSerialInspection({
      preQcRemaining: preQcRemainingCount,
      postQcFilled: activeInventoryBox?.filledCount ?? 0,
      boxCapacity,
      serialAlreadyRecorded: false,
    });
    if (rolled.startedNextPreQcCarton) {
      startNextPreQcCarton();
      const serialNumber = parseClampQcSerialAnchorNumber(trimmedSerial);
      if (serialNumber !== null) {
        updateSerialAnchor(serialNumber);
      }
    }
  };

  const laborIsWorking = labor.isWorking;
  const laborElapsedSeconds = labor.elapsedSeconds;
  const activeLaborElapsedLabel =
    laborIsWorking ? formatManufacturingLaborDuration(laborElapsedSeconds) : null;

  const laborSubtitleLine =
    laborIsWorking
      ? `${formatManufacturingLaborDuration(laborElapsedSeconds)} elapsed · ${workOrderContext.workOrder}`
      : lastLaborSummary
        ? `Last session: ${formatManufacturingLaborDuration(lastLaborSummary.netSeconds)} net time${
            lastLaborSummary.idleTimeout ? " (idle timeout)" : ""
          }.`
        : "";

  const dismissInspectionDetailModal = (): void => {
    inspectionSlotReserveGenerationRef.current += 1;
    setInspectionDetailModalOpen(false);
    setInspectionModalInductanceMeasurementRequired(null);
    setInspectionSlotSamplingMeta(null);
    setFormError(null);
    setInspectionPersistBanner(null);
    setNewPhysicalFailReasonDraft("");
    setAppendFailReasonMessage(null);
    setModalInductanceInput("");
    setModalUnloadedInput("");
    setModalLoadedInput("");
    setModalLoadedWithTesterInput("");
    failKeypadStarSequenceRef.current = { active: false, digits: "" };
    sessionRescanArmedSerialKeyRef.current = null;
    setDraft(emptyDraft());
  };
  dismissInspectionModalRef.current = dismissInspectionDetailModal;

  const launchClampQcInspectionDetailModal = (
    trimmedSerial: string,
    sessionRowForPrefill: InspectionTableRow | null = null,
  ): void => {
    if (bundle == null) {
      setFormError("Work order data is not loaded yet. Wait for the job to finish loading, then try again.");
      return;
    }
    const format = validateClampQcInspectionSerialFormat(trimmedSerial, {
      allowNonNumeric: allowNonNumericInspectionSerial,
    });
    if (!format.ok) {
      setFormError(format.error);
      return;
    }
    lastScannedSerialTextForJobRef.current = format.serial;
    const serialKey = inspectionSerialToStableKey(format.serial, allowNonNumericInspectionSerial);
    if (serialKey === null) {
      setFormError(
        allowNonNumericInspectionSerial
          ? "Enter or scan a serial."
          : "Serial must be a non-negative integer.",
      );
      return;
    }
    if (hubSettings == null) {
      setFormError(
        "Clamp QC hub settings could not be loaded. Open Clamp QC Settings and save a complete configuration, or fix the API error, before opening inspection.",
      );
      return;
    }
    setFormError(null);
    setInspectionPersistBanner(null);
    setNewPhysicalFailReasonDraft("");
    setAppendFailReasonMessage(null);
    const unloadedPrefill =
      sessionRowForPrefill != null
        ? clampQcFormatMeasurementPrefill(sessionRowForPrefill.unloadedLine)
        : sanitizeDecimalMeasurementInput(sessionUnloadedLine);
    const loadedPrefill =
      sessionRowForPrefill != null
        ? clampQcFormatMeasurementPrefill(sessionRowForPrefill.loadedLineBeforeTester)
        : "";
    const inductancePrefill =
      sessionRowForPrefill != null ? clampQcFormatMeasurementPrefill(sessionRowForPrefill.inductance) : "";
    const failReasonsPrefill =
      sessionRowForPrefill != null
        ? clampQcFailReasonLabelsFromSessionRow(sessionRowForPrefill.failReason)
        : [];
    setModalInductanceInput(inductancePrefill);
    setModalUnloadedInput(unloadedPrefill);
    setModalLoadedInput(loadedPrefill);
    const testerLoadedPrefill =
      sessionRowForPrefill != null
        ? clampQcFormatMeasurementPrefill(sessionRowForPrefill.loadedLineWithTester ?? null)
        : "";
    setModalLoadedWithTesterInput(testerLoadedPrefill);
    setDraft(() => ({
      ...emptyDraft(),
      serial: format.serial,
      unloadedLine: unloadedPrefill,
      loadedLineBeforeTester: loadedPrefill,
      loadedLineWithTester: testerLoadedPrefill,
      inductance: inductancePrefill,
      failReasons: failReasonsPrefill,
      lineHarvestFail: sessionRowForPrefill?.lineHarvestFail ?? null,
    }));
    setInspectionModalInductanceMeasurementRequired(null);
    setInspectionSlotSamplingMeta(null);
    setInspectionDetailModalOpen(true);
    armClampQcFloorKeyboardCooldownOnPage();

    const reserveGeneration = (inspectionSlotReserveGenerationRef.current += 1);
    const buildOrderId = bundle.buildOrderId;

    void (async () => {
      try {
        const reserveResponse = await postClampQcInspectionSlotReserve({ buildOrderId, serialKey });
        if (reserveGeneration !== inspectionSlotReserveGenerationRef.current) {
          return;
        }
        setInspectionModalInductanceMeasurementRequired(reserveResponse.requiresInductance);
        setInspectionSlotSamplingMeta({
          slotOrdinal: reserveResponse.slotOrdinal,
          plannedQuantity: reserveResponse.plannedQuantity,
          inductanceTestingTargetCount: reserveResponse.inductanceTestingTargetCount,
          inductanceTestingPercentPerWorkOrder: reserveResponse.inductanceTestingPercentPerWorkOrder,
        });
        if (!reserveResponse.requiresInductance) {
          setModalInductanceInput("");
          setDraft((previous) => ({ ...previous, inductance: "" }));
        }
      } catch (error) {
        if (reserveGeneration !== inspectionSlotReserveGenerationRef.current) {
          return;
        }
        const message =
          error instanceof ApiError
            ? error.status === 404 && error.message === "Not found."
              ? "The hub API did not handle the inspection-slot request. Deploy the latest integrator build, or confirm your browser reaches this app’s API (not a stale static host)."
              : error.message
            : "Could not reserve inductance sampling for this serial.";
        setFormError(message);
        setInspectionSlotSamplingMeta(null);
        setInspectionModalInductanceMeasurementRequired(null);
      }
    })();
  };

  const continueClampQcSerialPreflight = async (trimmedSerial: string): Promise<void> => {
    setFormError(null);
    try {
      const existsPayload = await fetchClampQcInspectionExists(trimmedSerial);
      if (existsPayload.exists) {
        treatNextPersistAsNewClampRowRef.current = false;
        setDuplicateSerialIncidentOpen(false);
        setSerialOverwriteConfirmation({
          trimmedSerial,
          recordedForBuildOrderId: existsPayload.recordedForBuildOrderId,
          recordedWorkOrderLabel: existsPayload.recordedWorkOrderLabel,
          recordedPurchaseOrderNumber: existsPayload.recordedPurchaseOrderNumber,
          sgsQcAtIso: existsPayload.sgsQcAtIso,
          sku: existsPayload.sku,
          inspectionResult: existsPayload.inspectionResult,
        });
        return;
      }
      startNextCartonsForNewSerialIfNeeded(trimmedSerial);
      if (clampQcSerialScanWouldExceedWorkOrderQuantity(null)) {
        setFormError(CLAMP_QC_WORK_ORDER_QUANTITY_CAP_ERROR);
        return;
      }
      treatNextPersistAsNewClampRowRef.current = true;
      launchClampQcInspectionDetailModal(trimmedSerial);
    } catch (error) {
      const message =
        error instanceof ApiError
          ? error.message
          : "Could not check whether this serial already has a saved inspection.";
      setFormError(message);
    }
  };

  const continueClampQcSerialAfterAnchorCheck = async (trimmedSerial: string): Promise<void> => {
    const serialNumber = parseClampQcSerialAnchorNumber(trimmedSerial);
    if (serialNumber !== null && !preQcCartonEmpty) {
      const currentAnchor = serialAnchorRef.current;
      if (currentAnchor === null) {
        updateSerialAnchor(serialNumber);
      } else if (clampQcSerialIsOutsideAnchorRange(currentAnchor, serialNumber)) {
        setSerialAnchorConfirmation({
          currentSerial: trimmedSerial,
          serialNumber,
          previousAnchor: currentAnchor,
        });
        return;
      }
    }
    await continueClampQcSerialPreflight(trimmedSerial);
  };

  const attemptOpenClampQcInspectionDetailModalFromSerialEntry = async (
    serialOverride?: string,
  ): Promise<void> => {
    if (bundle == null) {
      setFormError("Work order data is not loaded yet. Wait for the job to finish loading, then try again.");
      return;
    }
    if (
      shouldIgnoreClampQcFloorSerialScan({
        submitInFlight: clampQcSerialSubmitInFlightRef.current,
        inspectionDetailModalOpen,
        keyboardBlockedUntilMs: clampQcFloorKeyboardBlockedUntilRef.current,
      })
    ) {
      return;
    }
    if (!tryBeginClampQcSerialSubmit(clampQcSerialSubmitInFlightRef)) {
      return;
    }
    armClampQcFloorKeyboardCooldownOnPage();
    setInspectSerialPreflightBusy(true);
    try {
      const trimmedSerial = (serialOverride ?? draft.serial).trim();
      if (trimmedSerial === "") {
        setFormError("Serial is required.");
        return;
      }
      const boxHex = parseInventoryBoxIdFromScan(trimmedSerial);
      if (boxHex != null && inventoryBoxFlowEnabled) {
        setDraft((previous) => ({ ...previous, serial: "" }));
        await resumeInventoryBoxFromScan(boxHex);
        return;
      }
      if (inventoryBoxFlowEnabled && (activeInventoryBox == null || activeInventoryBox.sealed)) {
        setFormError("Print or scan an open post-QC box label before scanning clamp serials.");
        return;
      }
      const format = validateClampQcInspectionSerialFormat(trimmedSerial, {
        allowNonNumeric: allowNonNumericInspectionSerial,
      });
      if (!format.ok) {
        setFormError(format.error);
        return;
      }
      const serialKey = inspectionSerialToStableKey(format.serial, allowNonNumericInspectionSerial);
      if (serialKey === null) {
        setFormError(
          allowNonNumericInspectionSerial
            ? "Enter or scan a serial."
            : "Serial must be a non-negative integer.",
        );
        return;
      }
      const dupSession = sessionEntries.some((entry) => entry.serial === serialKey);
      if (dupSession) {
        setFormError(null);
        setSessionRescanConfirmation({ trimmedSerial: format.serial, serialKey });
        return;
      }
      if (hubSettings == null) {
        setFormError(
          "Clamp QC hub settings could not be loaded. Open Clamp QC Settings and save a complete configuration, or fix the API error, before opening inspection.",
        );
        return;
      }

      const previousSerialText = (() => {
        const sessionLast = lastScannedSerialTextForJobRef.current?.trim() ?? "";
        if (sessionLast !== "") {
          return sessionLast;
        }
        const serverLast = serverMostRecentSerialRef.current?.trim() ?? "";
        return serverLast !== "" ? serverLast : null;
      })();

      if (
        previousSerialText !== null &&
        previousSerialText !== format.serial &&
        clampQcSerialYmPrefixesDiffer(previousSerialText, format.serial)
      ) {
        setSerialYmMismatchConfirmation({
          previousSerial: previousSerialText,
          currentSerial: format.serial,
        });
        return;
      }

      await continueClampQcSerialAfterAnchorCheck(format.serial);
    } finally {
      endClampQcSerialSubmit(clampQcSerialSubmitInFlightRef);
      setInspectSerialPreflightBusy(false);
    }
  };

  openInspectionFromScannedSerialRef.current = (serial) => {
    if (
      shouldIgnoreClampQcFloorSerialScan({
        submitInFlight: clampQcSerialSubmitInFlightRef.current,
        inspectionDetailModalOpen,
        keyboardBlockedUntilMs: clampQcFloorKeyboardBlockedUntilRef.current,
      })
    ) {
      return;
    }
    setDraft((previous) => ({ ...previous, serial }));
    void attemptOpenClampQcInspectionDetailModalFromSerialEntry(serial);
  };

  const completeInspectionFromModal = (): void => {
    if (inspectionSaving) {
      return;
    }
    if (hubSettings == null) {
      setFormError(
        "Clamp QC hub settings could not be loaded. Save settings on the Clamp QC Settings page or fix the API error before recording.",
      );
      return;
    }
    if (bundle == null) {
      setFormError("Work order data is not loaded yet. Wait for the job to finish loading, then try again.");
      return;
    }
    const trimmedSerial = draft.serial.trim();
    if (trimmedSerial === "") {
      setFormError("Serial is missing—close this dialog and scan again.");
      return;
    }
    const format = validateClampQcInspectionSerialFormat(trimmedSerial, {
      allowNonNumeric: allowNonNumericInspectionSerial,
    });
    if (!format.ok) {
      setFormError(format.error);
      return;
    }
    const serialKey = inspectionSerialToStableKey(format.serial, allowNonNumericInspectionSerial);
    if (serialKey === null) {
      setFormError(
        allowNonNumericInspectionSerial
          ? "Serial is not valid."
          : "Serial must be a non-negative integer.",
      );
      return;
    }
    const rescanArmed = sessionRescanArmedSerialKeyRef.current === serialKey;
    if (!clampQcSessionRescanAllowsModalSubmit(sessionEntries, serialKey, sessionRescanArmedSerialKeyRef.current)) {
      setFormError("That serial is already recorded in this session.");
      return;
    }
    const mechanicalFailSkipsOtherTests = clampQcMechanicalFailureSkipsOtherTests(draft.failReasons);
    if (inspectionModalInductanceMeasurementRequired === null && !mechanicalFailSkipsOtherTests) {
      setFormError("Wait for inductance sampling to finish loading for this serial.");
      return;
    }
    const inductanceSampled =
      inspectionModalInductanceMeasurementRequired === true && !mechanicalFailSkipsOtherTests;
    const committed: DraftInspection = {
      ...draft,
      inductance: inductanceSampled ? sanitizeDecimalMeasurementInput(modalInductanceInput) : "",
      unloadedLine: sanitizeDecimalMeasurementInput(modalUnloadedInput),
      loadedLineBeforeTester: sanitizeDecimalMeasurementInput(modalLoadedInput),
      loadedLineWithTester: sanitizeDecimalMeasurementInput(modalLoadedWithTesterInput),
    };
    const inductanceOutOfBand =
      inductanceSampled &&
      clampQcInductanceReadingIsOutOfBand(committed.inductance, bundle.sku, hubSettings);
    const lineHarvestRequired = clampQcInspectionLineHarvestRequiredForUiSubmit({
      physicalFailReasonLabels: committed.failReasons,
      inductanceSampled,
      inductanceOutOfBand,
    });
    const dragForSubmit = lineHarvestRequired
      ? clampQcDragFromUnloadedLoadedLines(committed.unloadedLine, committed.loadedLineBeforeTester)
      : null;
    const ladderForSubmit = clampQcDragLadderFromSettings(bundle.sku, hubSettings);
    const bandForSubmit = lineHarvestRequired
      ? clampQcDragBandForSku(dragForSubmit, bundle.sku, hubSettings)
      : "unknown";
    const explicitFailForSubmit = inspectionModalHasExplicitFailCriteria(
      committed,
      bundle.sku,
      hubSettings,
      { inductanceSampled },
    );
    if (lineHarvestRequired && ladderForSubmit != null) {
      if (bandForSubmit === "unknown" && !explicitFailForSubmit) {
        setFormError(
          "Enter unloaded line and loaded line before tester as numbers so drag can be checked against hub limits.",
        );
        return;
      }
    }
    const lineHarvestNeedsChoice = ladderForSubmit == null || bandForSubmit === "gray";
    if (lineHarvestRequired && lineHarvestNeedsChoice && committed.lineHarvestFail === null && !explicitFailForSubmit) {
      setFormError("Choose Line harvest Good or Failed.");
      return;
    }
    if (clampQcInductanceEntryBlocksInspectionSubmit(committed.inductance, inductanceSampled)) {
      if (committed.inductance.trim() === "") {
        setFormError("Enter an inductance reading — this unit is in the inductance sample.");
      } else if (clampQcInductanceReadingHasInvalidNumeric(committed.inductance)) {
        setFormError("Enter inductance as a number before recording this inspection.");
      } else {
        setFormError("Correct the inductance reading before recording this inspection.");
      }
      return;
    }
    const lineReadingsForPersist = validateClampQcInspectionLineReadingsForUiSubmit({
      unloadedRaw: committed.unloadedLine,
      loadedRaw: committed.loadedLineBeforeTester,
      lineHarvestRequired,
    });
    if (!lineReadingsForPersist.ok) {
      setFormError(lineReadingsForPersist.error);
      return;
    }
    const unloadedLineForPersist = lineReadingsForPersist.unloaded;
    const loadedLineForPersist = lineReadingsForPersist.loaded;
    if (
      lineHarvestRequired &&
      unloadedLineForPersist !== null &&
      clampQcUnloadedLineFailsAcceptance(unloadedLineForPersist, bundle.sku, hubSettings)
    ) {
      setFormError(
        "Unloaded line is outside the accepted band. Correct it before recording this inspection.",
      );
      return;
    }
    if (
      lineHarvestRequired &&
      bandForSubmit === "gray" &&
      parseOptionalFloat(committed.loadedLineWithTester) === null &&
      !explicitFailForSubmit
    ) {
      setFormError("Enter loaded line with tester as a number for this gray-band inspection.");
      return;
    }
    setFormError(null);
    setInspectionPersistBanner(null);
    const submittedAtIso = new Date().toISOString();
    const row = draftToRow(committed, submittedAtIso, serialKey, bundle.sku, hubSettings, {
      inductanceSampled,
    });
    if (row.result === "Passed" && !requireOpenInventoryBoxForPass()) {
      return;
    }
    const nextSessionRow: ClampQcCartonSessionRow = {
      ...row,
      clampSize: sessionClampSizeTag,
      cartonInventoryBoxHex:
        row.result === "Passed" && activeInventoryBox != null && !activeInventoryBox.sealed
          ? activeInventoryBox.hex
          : null,
    };
    const qcBuildOrderId = bundle.buildOrderId;
    const deviceSerial = trimmedSerial;

    const notifyInspectionComplete = (): void => {
      if (row.result === "Passed") {
        playClampQcInspectionCompleteSound("Passed");
        return;
      }
      playClampQcInspectionCompleteSound("Failed");
      showToast({
        tone: "warning",
        message: clampQcFailDispositionToastForInspectionDraft(committed, bundle.sku, hubSettings, {
          inductanceSampled,
        }),
      });
    };

    const applyAcceptedInspectionToSession = (): void => {
      setSessionUnloadedLine(committed.unloadedLine);
      setSessionEntries((previous) => {
        if (rescanArmed) {
          const withoutPrior = previous.filter((entry) => entry.serial !== serialKey);
          return [nextSessionRow, ...withoutPrior];
        }
        return [nextSessionRow, ...previous];
      });
      if (
        clampQcShouldApplyInspectionToCartonCounts({
          sessionRescan: rescanArmed,
          existingSavedInspection: treatNextPersistAsNewClampRowRef.current === false,
        }) &&
        packSizeDerivedFromClampLine != null &&
        packSizeDerivedFromClampLine > 0
      ) {
        const rolled = clampQcCartonsForSerialInspection({
          preQcRemaining: preQcRemainingCount,
          postQcFilled: activeInventoryBox?.filledCount ?? 0,
          boxCapacity: packSizeDerivedFromClampLine,
          serialAlreadyRecorded: false,
        });
        const nextCartons = applyClampQcInspectionToCartons({
          preQcRemaining: rolled.preQcRemaining,
          postQcFilled: rolled.postQcFilled,
          boxCapacity: packSizeDerivedFromClampLine,
          passed: row.result === "Passed",
        });
        setPreQcRemaining(nextCartons.preQcRemaining);
        setPreQcCountDraft(String(nextCartons.preQcRemaining));
      } else if (
        rescanArmed &&
        inventoryBoxFlowEnabled &&
        activeInventoryBox != null &&
        !activeInventoryBox.sealed &&
        packSizeDerivedFromClampLine != null &&
        packSizeDerivedFromClampLine > 0
      ) {
        const priorSessionRow = clampQcFindSessionEntryBySerialKey(sessionEntries, serialKey);
        const priorPassed = priorSessionRow?.result.toLowerCase() === "passed";
        const newPassed = row.result === "Passed";
        const rescanCartons = applyClampQcRescanToCartons({
          postQcFilled: activeInventoryBox.filledCount,
          boxCapacity: packSizeDerivedFromClampLine,
          priorPassed,
          newPassed,
        });
        const nextBox = {
          ...activeInventoryBox,
          filledCount: rescanCartons.postQcFilled,
        };
        setActiveInventoryBox(nextBox);
        syncOpenInventoryBoxCache(nextBox.buildOrderId, nextBox);
      }
      sessionRescanArmedSerialKeyRef.current = null;
      labor.touchActivity();
      dismissInspectionDetailModal();
      if (laborPersistEligible && !rescanArmed) {
        void labor
          .recordScan({
            deviceSerial,
            target: { kind: "build_order", id: qcBuildOrderId },
          })
          .catch((error) => {
            const message =
              error instanceof ApiError
                ? error.message
                : "Could not record this unit for labor allocation (device_scan).";
            setLaborScanBanner({ tone: "warning", message });
          });
      }
    };

    const inspectionPersistEligible =
      !clampQcIsLocalSandboxSlug(slug) && hubClampQcWritesAllowed && laborPersistEligible;

    if (!inspectionPersistEligible) {
      applyAcceptedInspectionToSession();
      notifyInspectionComplete();
      armClampQcFloorKeyboardCooldownOnPage();
      return;
    }

    const maxSerialLen = 200;
    const serialTextForApi =
      trimmedSerial.length > maxSerialLen ? trimmedSerial.slice(0, maxSerialLen) : trimmedSerial;
    setInspectionSaving(true);
    armClampQcFloorKeyboardCooldownOnPage();
    void postClampQcInspectionRecord({
      buildOrderId: bundle.buildOrderId,
      serialKey,
      serialText: serialTextForApi,
      submittedAtIso,
      sku: bundle.sku,
      inductanceMeasurementRequired: inductanceSampled,
      physicalFailReasonLabels: [...committed.failReasons],
      sgsInductance: inductanceSampled ? row.inductance : null,
      sgsLineHarvestPerformed: lineHarvestPerformedForHubPersist(),
      sgsLineHarvestPass: row.lineHarvestFail === null ? null : !row.lineHarvestFail,
      sgsLineHarvestDrag: row.delta,
      sgsLineHarvestTester: row.testerA,
      sgsUnloadedLine: unloadedLineForPersist,
      sgsLoadedLineBeforeTester: loadedLineForPersist,
      sgsLoadedLineWithTester:
        lineHarvestRequired && bandForSubmit === "gray"
          ? parseOptionalFloat(committed.loadedLineWithTester)
          : null,
      inventoryBoxHex: row.result === "Passed" ? activeInventoryBox?.hex ?? null : null,
    })
      .then((response) => {
        if (
          response.boxFilledCount != null &&
          activeInventoryBox != null &&
          response.inventoryBoxHex === activeInventoryBox.hex
        ) {
          const nextBox = {
            ...activeInventoryBox,
            filledCount: response.boxFilledCount,
          };
          setActiveInventoryBox(nextBox);
          syncOpenInventoryBoxCache(nextBox.buildOrderId, nextBox);
          invalidateCartonPassedRows(nextBox.hex, nextBox.buildOrderId);
        } else if (
          activeInventoryBox != null &&
          (row.result === "Passed" || response.inventoryBoxHex === activeInventoryBox.hex)
        ) {
          invalidateCartonPassedRows(activeInventoryBox.hex, bundle.buildOrderId);
        }
        applyAcceptedInspectionToSession();
        notifyInspectionComplete();
        const slugKeyTrimmed = slug?.trim();
        const bumpBaselineComplete = treatNextPersistAsNewClampRowRef.current;
        treatNextPersistAsNewClampRowRef.current = true;

        const patchWorkOrdersListQuantity = (): void => {
          if (bundle == null || !bumpBaselineComplete) {
            return;
          }
          queryClient.setQueryData<ClampQcHubWorkOrderListItem[]>(CLAMPQC_HUB_WORK_ORDERS_QUERY_KEY, (previous) => {
            if (previous == null) {
              return previous;
            }
            return previous.map((listRow) =>
              listRow.buildOrderId === bundle.buildOrderId
                ? { ...listRow, quantityComplete: Math.max(0, listRow.quantityComplete + 1) }
                : listRow,
            );
          });
        };

        if (slugKeyTrimmed !== undefined && slugKeyTrimmed !== "") {
          queryClient.setQueryData<ClampQcWorkOrderBundle>(["clamp-qc", "work-order", slugKeyTrimmed], (previous) => {
            if (previous == null || !bumpBaselineComplete) {
              return previous;
            }
            return {
              ...previous,
              quantityComplete: Math.max(0, previous.quantityComplete + 1),
            };
          });

          patchWorkOrdersListQuantity();

          void queryClient.invalidateQueries({
            queryKey: ["clamp-qc", "work-order", slugKeyTrimmed],
            refetchType: "none",
          });
        } else {
          patchWorkOrdersListQuantity();
        }

        void queryClient.invalidateQueries({
          queryKey: CLAMPQC_HUB_WORK_ORDERS_QUERY_KEY,
          refetchType: "none",
        });
      })
      .catch((error) => {
        playClampQcInspectionSubmitErrorSound();
        treatNextPersistAsNewClampRowRef.current = true;
        const message =
          error instanceof ApiError
            ? error.message
            : "Could not save this inspection. Try Record inspection again.";
        setInspectionPersistBanner({ tone: "warning", message });
        setFormError(message);
      })
      .finally(() => {
        setInspectionSaving(false);
      });
  };

  const lineHarvestNeedsOperatorChoice = modalDragLadder === null || modalLineHarvestBand === "gray";
  const inspectionModalInductanceSampled =
    inspectionModalInductanceMeasurementRequired === true && !inspectionModalMechanicalFailSkipsOtherTests;
  const inspectionModalInductanceOutOfBand =
    inspectionModalInductanceSampled &&
    hubSettings != null &&
    bundle != null &&
    clampQcInductanceReadingIsOutOfBand(modalInductanceInput, bundle.sku, hubSettings);
  const inspectionModalLineHarvestRequired =
    !inspectionModalMechanicalFailSkipsOtherTests && !inspectionModalInductanceOutOfBand;
  const inspectionModalInductanceSectionDisabled =
    inspectionModalMechanicalFailSkipsOtherTests || inspectionModalInductanceMeasurementRequired !== true;
  const inspectionModalLineHarvestSectionDisabled = !inspectionModalLineHarvestRequired;

  return (
    <>
      <div className="space-y-6">
      {clampQcHubSettingsQuery.isError ? (
        <div
          className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950"
          role="alert"
        >
          <p className="font-medium">Clamp QC hub settings are not available</p>
          <p className="mt-1 text-amber-900">
            {clampQcHubSettingsQuery.error instanceof ApiError
              ? clampQcHubSettingsQuery.error.message
              : "The hub could not load saved settings from the server."}
          </p>
        </div>
      ) : null}
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-2">
          <h1 className="text-xl font-semibold text-slate-900">Job workspace</h1>
          <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1 text-sm text-slate-600">
            <Link className="font-medium text-brand-700 hover:text-brand-800" to="/clamp-qc/inspection">
              Inspection
            </Link>
            <ChevronRight aria-hidden className="h-4 w-4 shrink-0 text-slate-400" strokeWidth={2} />
            <span aria-current="page" className="font-medium text-slate-900">
              {workOrderContext.workOrder}
            </span>
          </nav>
        </div>
        {inventoryBoxFlowEnabled && boxCapacity != null && boxCapacity > 0 ? (
          <ClampQcHeaderPrinterSelect
            station={station}
            onStationChange={setStation}
            unavailableMessage={unavailableMessage}
            documents={documents}
          />
        ) : null}
      </header>

      <div
        className={cn(
          "sticky top-0 z-20 -mx-1 rounded-lg border border-slate-200 bg-white/95 px-4 py-3 shadow-sm backdrop-blur-sm",
        )}
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-lg font-semibold text-slate-900">
              {workOrderContext.workOrder}{" "}
              <span className="text-sm font-normal text-slate-500 tabular-nums">Build id {bundle.buildOrderId}</span>
            </p>
            <p className="mt-1 text-sm text-slate-600">
              <span className="font-mono text-xs text-slate-800">{bundle.sku}</span>
              <span className="text-slate-400"> · </span>
              {bundle.locationCode}
              <span className="text-slate-400"> · </span>
              Ship by {bundle.mfrShipByDate}
              {bundle.mfrReferenceNumber ? (
                <>
                  <span className="text-slate-400"> · </span>
                  Manufacturer reference {bundle.mfrReferenceNumber}
                </>
              ) : null}
            </p>
          </div>
          <div className="flex flex-col items-end gap-2 text-right text-sm">
            {showOtherOperatorsOnJob ? (
              <div className="flex flex-col items-end gap-0.5">
                <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                  {viewerKnown ? "Other operators on this job" : "Operators on this job"}
                </span>
                <PrePackCartonOperatorStack
                  operators={jobOperators}
                  viewerUserId={viewerUserId}
                  omitViewer={viewerKnown}
                />
              </div>
            ) : null}
            <p className="tabular-nums text-slate-700">
              <span className="font-medium text-slate-900">{baselineComplete.toLocaleString()}</span> units recorded
              complete
            </p>
            <p className="tabular-nums text-slate-600">
              <span className="font-medium">{remaining.toLocaleString()}</span> remaining to plan quantity ·{" "}
              <span className="font-medium">{unitsInOpenCarton}</span> in this carton
            </p>
          </div>
        </div>
        <div className="mt-3">
          <div className="h-2 overflow-hidden rounded-full bg-slate-100">
            <div
              className="h-full rounded-full bg-brand-600 transition-[width] duration-300"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-1">
        {PRIMARY_TABS.map((tab) => {
          const active = primaryTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => {
                if (tab.id !== "inspection") {
                  setSerialOverwriteConfirmation(null);
                  setDuplicateSerialIncidentOpen(false);
                }
                if (tab.id !== "inspection" && inspectionDetailModalOpen) {
                  dismissInspectionDetailModal();
                }
                setPrimaryTab(tab.id);
              }}
              className={cn(
                "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                active ? "bg-brand-50 text-brand-700" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900",
              )}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {primaryTab === "inspection" ? (
        <div className="grid gap-6 lg:grid-cols-5 lg:items-start">
          <div className="space-y-6 lg:col-span-3">
            <Card>
              <CardHeader>
                <CardTitle>New inspection</CardTitle>
              </CardHeader>
              <CardBody className="space-y-4">
                {formError && !inspectionDetailModalOpen ? (
                  <p className="text-sm text-rose-600">{formError}</p>
                ) : null}
                {workOrderQuantityCapReached ? (
                  <div
                    role="status"
                    className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950"
                  >
                    This work order already has {baselineComplete.toLocaleString()} of{" "}
                    {jobQuantity.toLocaleString()} units recorded (pass or fail). Scanning a new serial is blocked; you
                    can still re-inspect a serial already saved on this job.
                  </div>
                ) : null}
                {inspectionPersistBanner ? (
                  <div
                    role="status"
                    className={cn(
                      "rounded-md border px-3 py-2 text-sm",
                      inspectionPersistBanner.tone === "warning"
                        ? "border-amber-300 bg-amber-50 text-amber-950"
                        : "border-emerald-300 bg-emerald-50 text-emerald-950",
                    )}
                  >
                    {inspectionPersistBanner.message}
                  </div>
                ) : null}
                {inventoryBoxFlowEnabled && activeInventoryBox == null ? (
                  <div
                    role="status"
                    className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950"
                  >
                    Print a box label or scan an open box Data Matrix before recording passed units
                    {inventoryLabelSku != null ? ` (${inventoryLabelSku})` : ""}.
                  </div>
                ) : null}
                <form
                  className="space-y-4"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void attemptOpenClampQcInspectionDetailModalFromSerialEntry();
                  }}
                >
                  <div
                    role="status"
                    className={cn(
                      "rounded-md border px-3 py-2 text-sm text-slate-800",
                      inspectionClampFromSku === "unknown"
                        ? "border-amber-200 bg-amber-50"
                        : "border-brand-100 bg-brand-50",
                    )}
                  >
                    {inspectionClampFromSku === "large" ? (
                      <>
                        You should be inspecting a <span className="font-semibold">LARGE CLAMP</span> (SKU starts with{" "}
                        <span className="font-mono font-medium">90-003</span>).
                      </>
                    ) : inspectionClampFromSku === "small" ? (
                      <>
                        You should be inspecting a <span className="font-semibold">SMALL CLAMP</span> (SKU starts with{" "}
                        <span className="font-mono font-medium">90-002</span>).
                      </>
                    ) : (
                      <>
                        This SKU does not begin with <span className="font-mono">90-002</span> or{" "}
                        <span className="font-mono">90-003</span>—confirm the correct clamp line with a supervisor before
                        recording units.
                      </>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="qc-serial">Serial</Label>
                    <Input
                      ref={serialInputRef}
                      id="qc-serial"
                      value={draft.serial}
                      onChange={(event) => setDraft((previous) => ({ ...previous, serial: event.target.value }))}
                      className="mt-1 min-h-12 text-base tabular-nums"
                      inputMode={allowNonNumericInspectionSerial ? "text" : "numeric"}
                      autoComplete="off"
                      disabled={inspectSerialPreflightBusy}
                      {...FLOOR_PAGE_SCAN_TARGET_PROPS}
                    />
                    <p className="mt-1 text-xs text-slate-500">
                      Scan or type the serial, then press Enter. The remaining fields open in a full-screen step for
                      touch entry.
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <Button
                      type="submit"
                      disabled={inspectSerialPreflightBusy || bundle == null}
                      className="min-h-12 px-6 text-base font-semibold"
                    >
                      {inspectSerialPreflightBusy ? (
                        <span className="inline-flex items-center gap-2">
                          <Loader2 aria-hidden className="h-5 w-5 shrink-0 animate-spin" />
                          Checking serial…
                        </span>
                      ) : (
                        "Continue inspection"
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      className="min-h-12 px-6 text-base"
                      onClick={() => {
                        setFormError(null);
                        setSerialOverwriteConfirmation(null);
                        setDuplicateSerialIncidentOpen(false);
                        setDraft(emptyDraft());
                      }}
                    >
                      Clear serial
                    </Button>
                    {inventoryBoxFlowEnabled ? (
                      <Button
                        type="button"
                        variant="secondary"
                        className="min-h-12 px-6 text-base"
                        disabled={
                          activeInventoryBox == null ||
                          activeInventoryBox.sealed ||
                          removeClampFromCartonMutation.isPending
                        }
                        onClick={() => {
                          setRemoveFromCartonError(null);
                          setRemoveFromCartonSerialDraft("");
                          setRemoveFromCartonModalOpen(true);
                        }}
                      >
                        Remove clamp from carton
                      </Button>
                    ) : null}
                  </div>
                </form>
              </CardBody>
            </Card>
            <ClampQcRecentFailsCard rows={sessionFailedRows} />
          </div>

          <div className="space-y-4 lg:col-span-2">
            {slug != null ? (
              <ClampQcFinishToStockCard slug={slug} />
            ) : null}
            {boxCapacity != null && boxCapacity > 0 ? (
              <ClampQcCartonTrackersCard
                boxCapacity={boxCapacity}
                preQcRemaining={preQcRemainingCount}
                preQcDraft={
                  preQcRemaining !== null ? preQcCountDraft : String(preQcRemainingCount)
                }
                serialAnchor={serialAnchor}
                inventoryBox={inventoryBoxFlowEnabled ? activeInventoryBox : null}
                onPreQcFocus={() => {
                  manualPreQcFieldDirtyRef.current = true;
                }}
                onPreQcBlur={() => {
                  applyManualPreQcUnits();
                }}
                onPreQcDraftChange={setPreQcCountDraft}
                onStartNextPreQcCarton={startNextPreQcCarton}
                inventoryBoxFlowEnabled={inventoryBoxFlowEnabled}
                inventoryBoxSessionStarted={inventoryBoxSessionStarted}
                selectedPrinter={selectedZplPrinter}
                inventoryBoxBusy={inventoryBoxMutationBusy}
                onOpenAndPrintBox={openInventoryBoxForJob}
                onReprintBoxLabel={reprintActiveInventoryBoxLabel}
                onSealBox={sealActiveInventoryBox}
                onStartNextBox={startNextInventoryBoxAfterSeal}
              />
            ) : null}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">
                  This carton ({unitsInOpenCarton})
                </CardTitle>
              </CardHeader>
              <CardBody className="max-h-[280px] overflow-auto p-2">
                <InspectionTable
                  rows={cartonRowsForTable}
                  showClampSize
                  hideCartonColumn
                  emptyMessage={
                    activeInventoryBox == null || activeInventoryBox.sealed
                      ? "No open post-QC carton. Print or scan a box label to start packing passes."
                      : "No passed units in this open box yet."
                  }
                />
              </CardBody>
            </Card>
          </div>
        </div>
      ) : null}

      {primaryTab === "timesheet" ? (
        <div className="space-y-6">
            <Card>
            <CardHeader>
              <CardTitleWithHelp
                hoverHint={`Matches Manufacturing-Dashboard laborTracking: this job uses labor page key ${laborPage}. You are clocked in automatically when the job opens (unless hub mock-up blocks writes or you lack write access). If you are already clocked in elsewhere, this page does not load. When labor persistence is on, each submitted inspection also records labor.device_scan for build order id ${String(bundle.buildOrderId)} so session time can be split across scanned units; that table is for labor only, not the hub quantity complete tally (quality.clamp).`}
              >
                Labor tracking
              </CardTitleWithHelp>
            </CardHeader>
            <CardBody className="space-y-5">
              <p className="text-xs text-slate-500">
                While you are clocked in on this job, pointer movement, typing, scrolling, and focus changes anywhere on
                this page reset the <span className="font-semibold">client</span> idle clock (throttled to about once every
                two seconds so the browser stays responsive). The server session still receives a heartbeat about every{" "}
                {(labor.heartbeatIntervalMs / 1000).toString()} seconds, which updates{" "}
                <span className="font-mono">last_heartbeat_at</span>. Idle exit runs after{" "}
                <span className="font-semibold">{Math.round(labor.idleTimeoutMs / 60000)} minutes</span> without that
                activity.
                {!hubClampQcWritesAllowed ? (
                  <>
                    {" "}
                    <span className="font-medium text-amber-800">
                      Your hub role has read-only access to Clamp quality control: timers stay on this device only until
                      you receive write access on this module (read plus write or module administrator tier).
                    </span>
                  </>
                ) : laborRemotePersistenceDisabled("clampqc") ? (
                  <>
                    {" "}
                    <span className="font-medium text-slate-800">
                      Local dev: labor is simulated in the browser only (no Supabase start, heartbeat, end, or device_scan).
                      Set <span className="font-mono text-xs">VITE_CLAMP_QC_USE_SUPABASE_LABOR=true</span> in{" "}
                      <span className="font-mono">ui/.env</span> and restart Vite to hit the real labor APIs.
                    </span>
                  </>
                ) : labor.banner?.badgeLabel === "No labor writes" ? (
                  <>
                    {" "}
                    <span className="font-medium text-amber-800">
                      Hub mock-up policy blocks labor writes: session start, end, heartbeats, and device scans do not
                      update the database yet (see Global app settings <span className="font-mono">hub.mockup</span>).
                    </span>
                  </>
                ) : (
                  <>
                    {" "}
                    You are clocked in automatically when this job opens. Timers use{" "}
                    <span className="font-mono">labor.device_session</span> with page key{" "}
                    <span className="font-mono text-xs">{laborPage}</span>. You clock out after idle timeout or when you
                    leave this job.
                  </>
                )}
              </p>

              {labor.banner ? (
                <div
                  role="status"
                  className={cn(
                    "rounded-md border px-3 py-2 text-sm",
                    labor.banner.tone === "warning"
                      ? "border-amber-300 bg-amber-50 text-amber-950"
                      : "border-emerald-300 bg-emerald-50 text-emerald-950",
                  )}
                >
                  {labor.banner.message}
                </div>
              ) : null}

              {laborScanBanner ? (
                <div
                  role="status"
                  className={cn(
                    "rounded-md border px-3 py-2 text-sm",
                    laborScanBanner.tone === "warning"
                      ? "border-amber-300 bg-amber-50 text-amber-950"
                      : "border-emerald-300 bg-emerald-50 text-emerald-950",
                  )}
                >
                  {laborScanBanner.message}
                </div>
              ) : null}

              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-3">
                <div className="text-sm text-slate-700">
                  <span className="font-medium text-slate-900">{operatorAttribution}</span>
                  <span className="text-slate-500"> · from hub account</span>
                </div>
                {laborIsWorking ? (
                  <span className="text-xs font-medium text-emerald-800">Clocked in on this job</span>
                ) : (
                  <span className="text-xs text-slate-600">Not clocked in</span>
                )}
              </div>

              {laborSubtitleLine ? (
                <p className="text-xs text-slate-600">{laborSubtitleLine}</p>
              ) : null}

              <div
                className={cn(
                  "rounded-lg border px-4 py-4",
                  laborIsWorking ? "border-brand-200 bg-brand-50" : "border-slate-200 bg-slate-50",
                )}
              >
                <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-slate-700">
                  <Clock className="h-4 w-4 text-slate-500" aria-hidden />
                  {laborIsWorking ? "Active labor session" : "No active session"}
                  {laborIsWorking ? (
                    <Loader2 className="h-4 w-4 animate-spin text-brand-600" aria-hidden />
                  ) : null}
                </div>
                {laborIsWorking && activeLaborElapsedLabel != null ? (
                  <div className="mt-3 space-y-1">
                    <span className="font-mono text-4xl tabular-nums text-brand-800">{activeLaborElapsedLabel}</span>
                    <p className="text-xs text-slate-600">
                      Moving the pointer, typing, scrolling, or focusing controls on this page resets idle time (shared labor
                      module, throttled). Each submitted inspection still records a labor device scan when persistence is on.
                      This session:{" "}
                      <span className="font-medium">{sessionEntries.length}</span> inspection entr
                      {sessionEntries.length === 1 ? "y" : "ies"} logged.
                    </p>
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-slate-600">
                    Open this job to clock in automatically. Elapsed time follows the same pattern as the Manufacturing
                    Dashboard labor tracker.
                  </p>
                )}
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Completed intervals</p>
                {laborIntervals.length === 0 ? (
                  <p className="mt-2 text-sm text-slate-600">
                    None yet—a completed segment appears here after idle timeout or when you leave this job and the labor
                    session closes.
                  </p>
                ) : (
                  <>
                    <div className="mt-2 overflow-x-auto rounded-md border border-slate-200">
                      <table className="min-w-[740px] w-full border-collapse text-left text-sm">
                        <thead>
                          <tr className="border-b border-slate-200 bg-slate-100 text-xs font-semibold uppercase tracking-wide text-slate-600">
                            <th className="px-3 py-2">Started</th>
                            <th className="px-3 py-2">Ended</th>
                            <th className="px-3 py-2">Net time</th>
                            <th className="px-3 py-2">Operator</th>
                            <th className="px-3 py-2">How ended</th>
                          </tr>
                        </thead>
                        <tbody>
                          {laborIntervals.map((row) => (
                            <tr key={row.id} className="border-b border-slate-100">
                              <td className="whitespace-nowrap px-3 py-2 align-top">{formatTimestamp(row.startedAtIso)}</td>
                              <td className="whitespace-nowrap px-3 py-2 align-top">{formatTimestamp(row.endedAtIso)}</td>
                              <td className="px-3 py-2 align-top font-mono text-xs tabular-nums">
                                {formatManufacturingLaborDuration(row.netSeconds)}
                              </td>
                              <td className="whitespace-nowrap px-3 py-2 align-top font-medium">{row.initials ?? "—"}</td>
                              <td className="whitespace-nowrap px-3 py-2 align-top text-slate-700">
                                {row.idleTimeout ? "Idle timeout" : "Session ended"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="mt-3 text-xs text-slate-600">
                      Segments summed (net seconds):{" "}
                      <span className="font-mono font-medium tabular-nums text-slate-900">
                        {formatManufacturingLaborDuration(completedLaborSecondsTotal)}
                      </span>
                    </p>
                  </>
                )}
              </div>

              <p className="text-xs text-slate-500">
                Changing jobs in the address bar resets this page and runs labor bootstrap again for the new slug.
              </p>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <CardTitleWithHelp
                titleClassName="text-base"
                hoverHint="Imported columns from the team spreadsheet (read-only)—includes completed-quantity bookkeeping from the QC file. New labor intervals use the card above instead."
              >
                Archived workbook sample intervals
              </CardTitleWithHelp>
            </CardHeader>
            <CardBody className="space-y-3">
              <div className="overflow-x-auto rounded-md border border-slate-200">
                <table className="min-w-[640px] w-full border-collapse text-left text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-100 text-xs font-semibold uppercase tracking-wide text-slate-600">
                      <th className="px-3 py-2">Date</th>
                      <th className="px-3 py-2">Time start</th>
                      <th className="px-3 py-2">Time stop</th>
                      <th className="px-3 py-2">Initials</th>
                      <th className="px-3 py-2 text-right tabular-nums">Completed</th>
                      <th className="px-3 py-2 text-right tabular-nums">With line test</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bundle.timesheetRows.map((row, index) => (
                      <tr key={`${row.initials}-${index}`} className="border-b border-slate-100">
                        <td className="px-3 py-2 tabular-nums">{row.dateIso}</td>
                        <td className="px-3 py-2 tabular-nums">{row.timeStart}</td>
                        <td className="px-3 py-2 tabular-nums">{row.timeStop}</td>
                        <td className="px-3 py-2 font-medium">{row.initials}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{row.completedUnits}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{row.withLineTest}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </div>
      ) : null}

      {primaryTab === "summary" ? (
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Large clamp</CardTitle>
            </CardHeader>
            <CardBody className="space-y-2 text-sm text-slate-700">
              <p>
                <span className="font-medium text-slate-900">{bundle.largePassedTotal}</span> units marked{" "}
                <span className="font-medium">Passed</span> on the Large tab in the source workbook (full run in that
                file).
              </p>
              <p>
                Job quantity from the workbook summary cell:{" "}
                <span className="font-semibold tabular-nums text-slate-900">
                  {workOrderContext.plannedQuantityFromWorkbook.toLocaleString()}
                </span>
              </p>
              <p className="text-xs text-slate-500">
                Book totals for the mock sample. Session rows are not included until a backend exists.
              </p>
            </CardBody>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Small clamp</CardTitle>
            </CardHeader>
            <CardBody className="text-sm text-slate-700">
              <p>No production rows were filled in on the Small tab in the sample file (template only).</p>
            </CardBody>
          </Card>
        </div>
      ) : null}

      {primaryTab === "reference" ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Inductance parameters</CardTitle>
            </CardHeader>
            <CardBody>
              <div className="overflow-x-auto rounded-md border border-slate-200">
                <table className="w-full border-collapse text-left text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-100 text-xs font-semibold uppercase tracking-wide text-slate-600">
                      <th className="px-3 py-2">Type</th>
                      <th className="px-3 py-2 text-right tabular-nums">Minimum</th>
                      <th className="px-3 py-2 text-right tabular-nums">Maximum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {CLAMP_QC_INDUCTANCE_LIMITS.map((row) => (
                      <tr key={row.label} className="border-b border-slate-100">
                        <td className="px-3 py-2">{row.label}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{row.min ?? "—"}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{row.max ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Reasons for failure</CardTitle>
            </CardHeader>
            <CardBody>
              <ul className="list-inside list-disc space-y-1 text-sm text-slate-700">
                {sortedPhysicalFailReasons.map((reason) => (
                  <li key={reason}>{reason}</li>
                ))}
              </ul>
              <p className="mt-3 text-xs text-slate-500">
                Alphabetical list: built-in workbook reasons plus any saved in hub settings for all operators.
              </p>
            </CardBody>
          </Card>
        </div>
      ) : null}
      </div>

      {serialYmMismatchConfirmation !== null ? (
        <div
          className="fixed inset-0 z-[65] flex items-center justify-center bg-black/50 p-4"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setSerialYmMismatchConfirmation(null);
            }
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="clamp-serial-ym-mismatch-title"
            className="w-full max-w-lg rounded-xl border border-slate-200 bg-white p-5 shadow-xl"
            onMouseDown={(event) => {
              event.stopPropagation();
            }}
          >
            <h2 id="clamp-serial-ym-mismatch-title" className="text-lg font-semibold text-slate-900">
              Confirm serial batch month
            </h2>
            <p className="mt-3 text-sm text-slate-700">
              The year or month in this scan does not match the most recent serial on this job. Confirm both labels are
              correct before continuing, or go back and fix the scan.
            </p>
            <dl className="mt-4 space-y-3 text-sm">
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                <dt className="font-medium text-slate-800">Most recent serial</dt>
                <dd className="mt-1 font-mono text-sm font-semibold text-slate-900">
                  {serialYmMismatchConfirmation.previousSerial}
                </dd>
                {clampQcSerialYmBatchLabel(serialYmMismatchConfirmation.previousSerial) !== null ? (
                  <dd className="mt-1 text-slate-600">
                    Batch month: {clampQcSerialYmBatchLabel(serialYmMismatchConfirmation.previousSerial)}
                  </dd>
                ) : null}
              </div>
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                <dt className="font-medium text-amber-950">Current scan</dt>
                <dd className="mt-1 font-mono text-sm font-semibold text-amber-950">
                  {serialYmMismatchConfirmation.currentSerial}
                </dd>
                {clampQcSerialYmBatchLabel(serialYmMismatchConfirmation.currentSerial) !== null ? (
                  <dd className="mt-1 text-amber-900">
                    Batch month: {clampQcSerialYmBatchLabel(serialYmMismatchConfirmation.currentSerial)}
                  </dd>
                ) : null}
              </div>
            </dl>
            <div className="mt-6 flex flex-wrap justify-end gap-3">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setSerialYmMismatchConfirmation(null);
                  setDraft((previous) => ({ ...previous, serial: "" }));
                  setFormError(null);
                  window.requestAnimationFrame(() => {
                    serialInputRef.current?.focus();
                  });
                }}
              >
                Correct serials
              </Button>
              <Button
                type="button"
                onClick={() => {
                  const details = serialYmMismatchConfirmation;
                  if (details === null) {
                    return;
                  }
                  setSerialYmMismatchConfirmation(null);
                  void continueClampQcSerialAfterAnchorCheck(details.currentSerial);
                }}
              >
                Both are correct
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {serialAnchorConfirmation !== null ? (
        <div
          className="fixed inset-0 z-[65] flex items-center justify-center bg-black/50 p-4"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setSerialAnchorConfirmation(null);
            }
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="clamp-serial-anchor-title"
            className="w-full max-w-lg rounded-xl border border-slate-200 bg-white p-5 shadow-xl"
            onMouseDown={(event) => {
              event.stopPropagation();
            }}
          >
            <h2 id="clamp-serial-anchor-title" className="text-lg font-semibold text-slate-900">
              Serial is outside this carton window
            </h2>
            <p className="mt-3 text-sm text-slate-700">
              This scan is more than {String(CLAMP_QC_SERIAL_ANCHOR_RANGE)} away from the first serial on this pre-QC
              carton. Confirm the number if this box has mixed serials, or go back and fix the scan.
            </p>
            <dl className="mt-4 space-y-3 text-sm">
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                <dt className="font-medium text-slate-800">Current carton window</dt>
                <dd className="mt-1 font-mono text-sm font-semibold text-slate-900">
                  {String(serialAnchorConfirmation.previousAnchor)} +/- {String(CLAMP_QC_SERIAL_ANCHOR_RANGE)}
                </dd>
              </div>
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                <dt className="font-medium text-amber-950">Current scan</dt>
                <dd className="mt-1 font-mono text-sm font-semibold text-amber-950">
                  {serialAnchorConfirmation.currentSerial}
                </dd>
              </div>
            </dl>
            <div className="mt-6 flex flex-wrap justify-end gap-3">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setSerialAnchorConfirmation(null);
                  setDraft((previous) => ({ ...previous, serial: "" }));
                  setFormError(null);
                  window.requestAnimationFrame(() => {
                    serialInputRef.current?.focus();
                  });
                }}
              >
                Correct serial
              </Button>
              <Button
                type="button"
                onClick={() => {
                  const details = serialAnchorConfirmation;
                  if (details === null) {
                    return;
                  }
                  updateSerialAnchor(details.serialNumber);
                  setSerialAnchorConfirmation(null);
                  void continueClampQcSerialPreflight(details.currentSerial);
                }}
              >
                Use this number
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {removeFromCartonModalOpen ? (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !removeClampFromCartonMutation.isPending) {
              setRemoveFromCartonModalOpen(false);
              setRemoveFromCartonError(null);
            }
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="remove-from-carton-title"
            className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-xl"
            onMouseDown={(event) => {
              event.stopPropagation();
            }}
          >
            <h2 id="remove-from-carton-title" className="text-lg font-semibold text-slate-900">
              Remove clamp from carton
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              Enter the clamp serial to remove it from your open box{" "}
              {activeInventoryBox != null ? (
                <span className="font-mono font-semibold text-slate-800">{activeInventoryBox.shortCode}</span>
              ) : null}
              . This does not change the QC pass or fail result for that unit.
            </p>
            {removeFromCartonError ? <p className="mt-3 text-sm text-rose-600">{removeFromCartonError}</p> : null}
            <form
              className="mt-4 space-y-4"
              onSubmit={(event) => {
                event.preventDefault();
                if (bundle == null || activeInventoryBox == null || activeInventoryBox.sealed) {
                  setRemoveFromCartonError("Open a post-QC box before removing a unit.");
                  return;
                }
                const trimmed = removeFromCartonSerialDraft.trim();
                if (trimmed === "") {
                  setRemoveFromCartonError("Serial is required.");
                  return;
                }
                removeClampFromCartonMutation.mutate({
                  hex: activeInventoryBox.hex,
                  buildOrderId: bundle.buildOrderId,
                  serialText: trimmed,
                });
              }}
            >
              <div>
                <Label htmlFor="remove-from-carton-serial">Clamp serial</Label>
                <Input
                  id="remove-from-carton-serial"
                  value={removeFromCartonSerialDraft}
                  onChange={(event) => setRemoveFromCartonSerialDraft(event.target.value)}
                  className="mt-1 min-h-12 text-base tabular-nums"
                  autoComplete="off"
                  disabled={removeClampFromCartonMutation.isPending}
                />
              </div>
              <div className="flex flex-wrap justify-end gap-3">
                <Button
                  type="button"
                  variant="secondary"
                  disabled={removeClampFromCartonMutation.isPending}
                  onClick={() => {
                    setRemoveFromCartonModalOpen(false);
                    setRemoveFromCartonError(null);
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={removeClampFromCartonMutation.isPending}>
                  {removeClampFromCartonMutation.isPending ? "Removing…" : "Remove from carton"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {sessionRescanConfirmation !== null ? (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setSessionRescanConfirmation(null);
            }
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="clamp-session-rescan-title"
            className="w-full max-w-lg rounded-xl border border-slate-200 bg-white p-5 shadow-xl"
            onMouseDown={(event) => {
              event.stopPropagation();
            }}
          >
            <h2 id="clamp-session-rescan-title" className="text-lg font-semibold text-slate-900">
              Serial already QC checked this session
            </h2>
            <p className="mt-3 text-sm text-slate-700">
              You already recorded a QC check for this serial during this session
              {bundle != null ? (
                <>
                  {" "}
                  on <span className="font-semibold text-slate-900">{bundle.context.workOrder}</span>
                </>
              ) : null}
              . Before updating, confirm this clamp is not a duplicate serial number on the line.
            </p>
            <p className="mt-3 rounded-md bg-slate-50 px-3 py-2 font-mono text-sm font-semibold text-slate-900">
              {sessionRescanConfirmation.trimmedSerial}
            </p>
            {(() => {
              const priorSessionRow = clampQcFindSessionEntryBySerialKey(
                sessionEntries,
                sessionRescanConfirmation.serialKey,
              );
              if (priorSessionRow == null) {
                return null;
              }
              const cartonSummary = clampQcSessionRescanCartonSummary({
                result: priorSessionRow.result,
                cartonInventoryBoxHex: priorSessionRow.cartonInventoryBoxHex,
                openCartonHex: activeInventoryBox?.hex,
              });
              const failReasonLabels = clampQcFailReasonLabelsFromSessionRow(priorSessionRow.failReason);
              const passed = priorSessionRow.result.trim().toLowerCase() === "passed";
              return (
                <ul className="mt-4 space-y-1.5 text-sm text-slate-600">
                  <li>
                    <span className="font-medium text-slate-800">Recorded in this session:</span>{" "}
                    {formatInspectionTimestamp(priorSessionRow.timestampIso)}
                  </li>
                  <li>
                    <span className="font-medium text-slate-800">{cartonSummary.heading}:</span>{" "}
                    {cartonSummary.detail}
                  </li>
                  <li>
                    <span className="font-medium text-slate-800">Unloaded line:</span>{" "}
                    {priorSessionRow.unloadedLine ?? "—"}
                  </li>
                  <li>
                    <span className="font-medium text-slate-800">Loaded line before tester:</span>{" "}
                    {priorSessionRow.loadedLineBeforeTester ?? "—"}
                  </li>
                  <li>
                    <span className="font-medium text-slate-800">Result:</span> {priorSessionRow.result}
                  </li>
                  {failReasonLabels.length > 0 ? (
                    <li>
                      <span className="font-medium text-slate-800">Fail reasons:</span>{" "}
                      {failReasonLabels.join("; ")}
                    </li>
                  ) : null}
                  {!passed ? (
                    <li className="text-slate-500">
                      Failed units are not shown in the &quot;This carton&quot; list — only passed units packed in the
                      open box appear there.
                    </li>
                  ) : cartonSummary.detail.includes("Different carton") ? (
                    <li className="text-slate-500">
                      The &quot;This carton&quot; list shows all passed units in the open box, including units from
                      earlier sessions on this job.
                    </li>
                  ) : null}
                </ul>
              );
            })()}
            <div className="mt-6 flex flex-wrap justify-end gap-3">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setSessionRescanConfirmation(null);
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => {
                  const confirmation = sessionRescanConfirmation;
                  if (confirmation === null) {
                    return;
                  }
                  const priorSessionRow = clampQcFindSessionEntryBySerialKey(sessionEntries, confirmation.serialKey);
                  treatNextPersistAsNewClampRowRef.current = false;
                  sessionRescanArmedSerialKeyRef.current = confirmation.serialKey;
                  setSessionRescanConfirmation(null);
                  launchClampQcInspectionDetailModal(confirmation.trimmedSerial, priorSessionRow ?? null);
                }}
              >
                Update QC check
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {serialOverwriteConfirmation !== null ? (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !duplicateSerialIncidentOpen) {
              setSerialOverwriteConfirmation(null);
              setDuplicateSerialIncidentOpen(false);
            }
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="clamp-serial-duplicate-title"
            className="w-full max-w-lg rounded-xl border border-slate-200 bg-white p-5 shadow-xl"
            onMouseDown={(event) => {
              event.stopPropagation();
            }}
          >
            {duplicateSerialIncidentOpen && bundle != null ? (
              <>
                <h2 id="clamp-serial-duplicate-title" className="text-lg font-semibold text-slate-900">
                  Open QC incident for duplicate serial
                </h2>
                <p className="mt-2 text-sm text-slate-600">
                  File a clamp quality incident for serial{" "}
                  <span className="font-mono font-semibold text-slate-900">
                    {serialOverwriteConfirmation.trimmedSerial}
                  </span>
                  . Adjust the issue text if needed before submitting.
                </p>
                <div className="mt-4">
                  <OpenIncidentForm
                    defaultType="clamp"
                    defaultSubtype={
                      clampInspectionSizeFromSku(bundle.sku) === "small"
                        ? "small"
                        : clampInspectionSizeFromSku(bundle.sku) === "large"
                          ? "large"
                          : "large"
                    }
                    serialPrefill={serialOverwriteConfirmation.trimmedSerial}
                    defaultIssue={clampQcDuplicateSerialIncidentIssue(serialOverwriteConfirmation)}
                    defaultIssueNotes={clampQcDuplicateSerialIncidentNotes(serialOverwriteConfirmation, bundle)}
                    onCancel={() => {
                      setDuplicateSerialIncidentOpen(false);
                    }}
                    onSuccess={() => {
                      const duplicateSerial = serialOverwriteConfirmation.trimmedSerial;
                      setSerialOverwriteConfirmation(null);
                      setDuplicateSerialIncidentOpen(false);
                      setDraft(emptyDraft());
                      setInspectionPersistBanner({
                        tone: "warning",
                        message: `QC incident opened for duplicate serial ${duplicateSerial}.`,
                      });
                    }}
                  />
                </div>
              </>
            ) : (
              <>
                <h2 id="clamp-serial-duplicate-title" className="text-lg font-semibold text-slate-900">
                  Serial already QC checked
                </h2>
                <p className="mt-3 text-sm text-slate-700">
                  This serial has a saved inspection in quality control. Review the prior record below before you
                  continue.
                </p>
                <p className="mt-3 rounded-md bg-slate-50 px-3 py-2 font-mono text-sm font-semibold text-slate-900">
                  {serialOverwriteConfirmation.trimmedSerial}
                </p>
                <ul className="mt-4 space-y-1.5 text-sm text-slate-600">
                  <li>
                    <span className="font-medium text-slate-800">Recorded on quality control:</span>{" "}
                    {formatTimestamp(serialOverwriteConfirmation.sgsQcAtIso)}
                  </li>
                  <li>
                    <span className="font-medium text-slate-800">Work order on prior row:</span>{" "}
                    {serialOverwriteConfirmation.recordedWorkOrderLabel != null &&
                    serialOverwriteConfirmation.recordedWorkOrderLabel !== ""
                      ? serialOverwriteConfirmation.recordedWorkOrderLabel
                      : serialOverwriteConfirmation.recordedForBuildOrderId != null
                        ? String(serialOverwriteConfirmation.recordedForBuildOrderId)
                        : "—"}
                  </li>
                  <li>
                    <span className="font-medium text-slate-800">Purchase order on prior row:</span>{" "}
                    {serialOverwriteConfirmation.recordedPurchaseOrderNumber != null &&
                    serialOverwriteConfirmation.recordedPurchaseOrderNumber !== ""
                      ? serialOverwriteConfirmation.recordedPurchaseOrderNumber
                      : "—"}
                  </li>
                  <li>
                    <span className="font-medium text-slate-800">Stock keeping unit on prior row:</span>{" "}
                    {serialOverwriteConfirmation.sku != null && serialOverwriteConfirmation.sku !== ""
                      ? serialOverwriteConfirmation.sku
                      : "—"}
                  </li>
                  <li>
                    <span className="font-medium text-slate-800">Prior result:</span>{" "}
                    {serialOverwriteConfirmation.inspectionResult ?? "—"}
                  </li>
                  {bundle != null ? (
                    <li>
                      <span className="font-medium text-slate-800">Open job:</span>{" "}
                      {bundle.context.workOrder} · purchase order {bundle.context.purchaseOrder}
                    </li>
                  ) : null}
                </ul>
                {bundle != null &&
                !clampQcDuplicateSerialOnOpenJob(serialOverwriteConfirmation, bundle) ? (
                  <p className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
                    The prior inspection belongs to a different work order. Updating the QC check from this job is not
                    allowed—open a QC incident or cancel and scan a different serial.
                  </p>
                ) : bundle != null ? (
                  <p className="mt-4 text-sm text-slate-700">
                    The prior row belongs to this job. You may update the QC check with a new inspection.
                  </p>
                ) : null}
                <div className="mt-6 flex flex-wrap justify-end gap-3">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setSerialOverwriteConfirmation(null);
                      setDuplicateSerialIncidentOpen(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setDuplicateSerialIncidentOpen(true);
                    }}
                  >
                    Open QC incident
                  </Button>
                  <Button
                    type="button"
                    disabled={bundle == null || !clampQcDuplicateSerialOnOpenJob(serialOverwriteConfirmation, bundle)}
                    onClick={() => {
                      const details = serialOverwriteConfirmation;
                      if (details === null || bundle == null) {
                        return;
                      }
                      if (!clampQcDuplicateSerialOnOpenJob(details, bundle)) {
                        return;
                      }
                      setSerialOverwriteConfirmation(null);
                      setDuplicateSerialIncidentOpen(false);
                      launchClampQcInspectionDetailModal(details.trimmedSerial);
                    }}
                  >
                    Update QC check
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}

      {inspectionDetailModalOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              dismissInspectionDetailModal();
            }
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="inspection-touch-modal-title"
            className="flex max-h-[calc(100dvh-1rem)] w-full max-w-[min(96rem,calc(100vw-1rem))] flex-col overflow-x-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
            onMouseDown={(event) => {
              event.stopPropagation();
            }}
          >
            <div className="shrink-0 border-b border-slate-200 bg-slate-50 px-4 py-4 sm:px-6">
              <h2 id="inspection-touch-modal-title" className="text-lg font-semibold text-slate-900 sm:text-xl">
                Inspection details
              </h2>
              <p className="mt-1 text-xs font-medium uppercase tracking-wide text-slate-500">Unit serial</p>
              <p className="font-mono text-2xl font-semibold tabular-nums text-slate-900">{draft.serial}</p>
              {formError ? <p className="mt-2 text-sm text-rose-600">{formError}</p> : null}
            </div>

            <form
              className="flex min-h-0 min-w-0 flex-1 flex-col"
              onSubmit={(event) => {
                event.preventDefault();
                completeInspectionFromModal();
              }}
            >
              <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden overflow-y-auto overscroll-contain px-3 py-4 sm:px-6 sm:py-5">
                <div className="grid min-h-0 min-w-0 flex-1 grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8 lg:items-start">
                  <section className="min-w-0 space-y-3" aria-label="Physical inspection">
                    <h3 className="border-b border-slate-200 pb-2 text-base font-semibold text-slate-900">
                      Physical inspection
                    </h3>
                    <div className="space-y-3">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <p className="text-sm font-medium text-slate-800">Fail reasons</p>
                        <HoverHintWithGlyph hoverHint={failReasonsKeypadHelpText()} />
                      </div>
                      <div className="grid grid-cols-1 gap-x-10 gap-y-2 rounded-lg border border-slate-200 bg-slate-50/80 p-3 sm:grid-cols-2 sm:gap-x-12">
                        {sortedPhysicalFailReasons.map((reason, index) => {
                          const checked = draft.failReasons.includes(reason);
                          const inputId = `qc-modal-fail-${String(index)}`;
                          const listPosition = String(index + 1);
                          return (
                            <label
                              key={reason}
                              htmlFor={inputId}
                              className="flex min-w-0 cursor-pointer items-start gap-2 rounded-md px-1.5 py-1.5 text-sm text-slate-800 hover:bg-white/80"
                            >
                              <span className="inline-flex shrink-0 items-start gap-1.5">
                                <input
                                  ref={index === 0 ? inspectionModalFirstFieldRef : undefined}
                                  id={inputId}
                                  type="checkbox"
                                  checked={checked}
                                  onKeyDown={(event) => {
                                    if (event.key === "Enter") {
                                      event.preventDefault();
                                      focusElementNextTick(
                                        inspectionModalInductanceMeasurementRequired === true
                                          ? inspectionModalInductanceFieldRef.current
                                          : inspectionModalUnloadedFieldRef.current,
                                      );
                                    }
                                  }}
                                  onChange={() => {
                                    setDraft((previous) => {
                                      const nextHas = previous.failReasons.includes(reason);
                                      return {
                                        ...previous,
                                        failReasons: nextHas
                                          ? previous.failReasons.filter((entry) => entry !== reason)
                                          : [...previous.failReasons, reason],
                                      };
                                    });
                                  }}
                                  className="mt-0.5 h-5 w-5 shrink-0 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                                />
                                <span
                                  className="mt-0.5 w-7 shrink-0 text-right text-xs font-medium tabular-nums text-slate-400"
                                  aria-hidden
                                >
                                  {listPosition}.
                                </span>
                              </span>
                              <span className="min-w-0 flex-1 leading-snug">{reason}</span>
                            </label>
                          );
                        })}
                      </div>
                      <div className="rounded-lg border border-dashed border-slate-300 bg-white p-3">
                        <Label htmlFor="qc-modal-fail-new">New reason for everyone</Label>
                        <p className="mt-1 text-xs text-slate-500">
                          Adds the reason to the shared Clamp QC list so every operator sees it here (alphabetical).
                          You do not need Clamp QC administrator access for this.
                        </p>
                        <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
                          <Input
                            id="qc-modal-fail-new"
                            value={newPhysicalFailReasonDraft}
                            onChange={(event) => setNewPhysicalFailReasonDraft(event.target.value)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                event.preventDefault();
                              }
                            }}
                            placeholder="Describe the failure"
                            className="min-h-12 flex-1 text-base"
                            maxLength={200}
                            disabled={!hubClampQcWritesAllowed || appendPhysicalFailReasonMutation.isPending}
                            autoComplete="off"
                          />
                          <Button
                            type="button"
                            variant="secondary"
                            className="min-h-12 shrink-0 font-semibold"
                            disabled={
                              !hubClampQcWritesAllowed ||
                              appendPhysicalFailReasonMutation.isPending ||
                              newPhysicalFailReasonDraft.trim() === ""
                            }
                            onClick={() => {
                              const trimmed = newPhysicalFailReasonDraft.trim();
                              if (trimmed === "") {
                                return;
                              }
                              setAppendFailReasonMessage(null);
                              appendPhysicalFailReasonMutation.mutate({ label: trimmed });
                            }}
                          >
                            {appendPhysicalFailReasonMutation.isPending ? "Saving…" : "Add for everyone"}
                          </Button>
                        </div>
                        {!hubClampQcWritesAllowed ? (
                          <p className="mt-2 text-xs text-amber-800">Read-only hub role: you cannot add new reasons.</p>
                        ) : null}
                        {appendFailReasonMessage ? (
                          <p className="mt-2 text-sm text-rose-600">{appendFailReasonMessage}</p>
                        ) : null}
                      </div>
                    </div>
                  </section>

                  <section className="min-w-0 space-y-3" aria-label="Inductance">
                    <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-200 pb-2">
                      <h3 className="mb-0 text-base font-semibold text-slate-900">Inductance</h3>
                      <HoverHintWithGlyph hoverHint={inductanceSectionHelpText(bundle.sku, hubSettings)} />
                    </div>
                    {inspectionModalMechanicalFailSkipsOtherTests ? (
                      <p className="text-sm text-slate-600">
                        Mechanical fail is selected — inductance is not required for this unit.
                      </p>
                    ) : null}
                    {inspectionModalInductanceMeasurementRequired === null ? (
                      <p className="text-sm text-slate-600">Reserving whether this unit is in the inductance sample…</p>
                    ) : null}
                    <div>
                      <Label htmlFor="qc-modal-inductance">Inductance</Label>
                      <Input
                        ref={inspectionModalInductanceFieldRef}
                        id="qc-modal-inductance"
                        value={modalInductanceInput}
                        onChange={(event) =>
                          setModalInductanceInput(sanitizeDecimalMeasurementInput(event.target.value))
                        }
                        onBlur={(event) => {
                          const value = sanitizeDecimalMeasurementInput(event.target.value);
                          setModalInductanceInput(value);
                          setDraft((previous) => ({ ...previous, inductance: value }));
                        }}
                        onKeyDown={(event) =>
                          measurementInputKeyDownWithEnterFocusNext(
                            event,
                            inspectionModalUnloadedFieldRef.current,
                          )
                        }
                        className="mt-2 min-h-14 text-base tabular-nums"
                        inputMode="decimal"
                        autoComplete="off"
                        disabled={inspectionModalInductanceSectionDisabled}
                        aria-invalid={
                          inspectionModalInductanceMeasurementRequired === true &&
                          clampQcInductanceReadingHasInvalidNumeric(modalInductanceInput)
                        }
                      />
                      {inspectionModalInductanceMeasurementRequired === true &&
                      clampQcInductanceReadingHasInvalidNumeric(modalInductanceInput) ? (
                        <p className="mt-1 text-sm text-rose-600">
                          Enter inductance as a number before you can record this inspection.
                        </p>
                      ) : null}
                    </div>
                  </section>

                  <section className="min-w-0 space-y-3" aria-label="Line harvesting">
                    <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-200 pb-2">
                      <h3 className="mb-0 text-base font-semibold text-slate-900">Line harvesting</h3>
                      <HoverHintWithGlyph hoverHint={lineHarvestSectionHelpText(bundle.sku, hubSettings)} />
                    </div>
                    {inspectionModalLineHarvestSectionDisabled ? (
                      <p className="text-sm text-slate-600">
                        {inspectionModalMechanicalFailSkipsOtherTests
                          ? "Mechanical fail is selected — line harvest is not required for this unit."
                          : "Inductance fail — line harvest is not required for this unit."}
                      </p>
                    ) : null}
                    {!inspectionModalLineHarvestSectionDisabled && modalDragLadder != null && modalLineHarvestBand === "pass" ? (
                      <p className="text-sm text-slate-600">Drag is in the pass band—line harvest passes automatically.</p>
                    ) : null}
                    {!inspectionModalLineHarvestSectionDisabled && modalDragLadder != null && modalLineHarvestBand === "fail" ? (
                      <p className="text-sm text-red-800">
                        Drag is outside the gray window—line harvest fails automatically.
                      </p>
                    ) : null}
                    {!inspectionModalLineHarvestSectionDisabled && modalDragLadder == null ? (
                      <p className="text-sm text-slate-600">
                        This job SKU does not use hub drag windows—choose Line harvest Good or Failed manually.
                      </p>
                    ) : null}
                    <div>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Label htmlFor="qc-modal-unloaded">Unloaded line</Label>
                        <HoverHintWithGlyph hoverHint="The amperage reading before placing the clamp on the line. It must be inside this prefix's unloaded band before you can record. Outside the band, Record stays disabled and the unit is not failed or scrapped. Remembered for the next clamp on the job." />
                      </div>
                      <Input
                        ref={inspectionModalUnloadedFieldRef}
                        id="qc-modal-unloaded"
                        value={modalUnloadedInput}
                        onChange={(event) =>
                          setModalUnloadedInput(sanitizeDecimalMeasurementInput(event.target.value))
                        }
                        onBlur={(event) => {
                          const value = sanitizeDecimalMeasurementInput(event.target.value);
                          setModalUnloadedInput(value);
                          setDraft((previous) => ({ ...previous, unloadedLine: value }));
                          setSessionUnloadedLine(value);
                        }}
                        onKeyDown={(event) =>
                          measurementInputKeyDownWithEnterFocusNext(
                            event,
                            inspectionModalLoadedFieldRef.current,
                          )
                        }
                        className="mt-2 min-h-14 text-base tabular-nums"
                        inputMode="decimal"
                        autoComplete="off"
                        disabled={inspectionModalLineHarvestSectionDisabled}
                      />
                      {!inspectionModalLineHarvestSectionDisabled &&
                      (() => {
                        const unloaded = parseOptionalFloat(modalUnloadedInput);
                        if (hubSettings == null || unloaded == null) {
                          return null;
                        }
                        if (!clampQcUnloadedLineFailsAcceptance(unloaded, bundle.sku, hubSettings)) {
                          return null;
                        }
                        const range = clampQcUnloadedLineAcceptanceRangeForSku(bundle.sku, hubSettings);
                        const bandText =
                          range == null ? "inside the accepted band" : `${String(range.min)}–${String(range.max)}`;
                        return (
                          <p className="mt-1 text-sm text-rose-600">
                            Unloaded line must be {bandText} before this inspection can be recorded.
                          </p>
                        );
                      })()}
                    </div>
                    <div>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Label htmlFor="qc-modal-loaded">Loaded line before tester</Label>
                        <HoverHintWithGlyph hoverHint="The amperage reading after placing the clamp on the line. This reading is not a pass or fail by itself. Pass or fail is the drop from unloaded line." />
                      </div>
                      <Input
                        ref={inspectionModalLoadedFieldRef}
                        id="qc-modal-loaded"
                        value={modalLoadedInput}
                        onChange={(event) =>
                          setModalLoadedInput(sanitizeDecimalMeasurementInput(event.target.value))
                        }
                        onBlur={(event) => {
                          const value = sanitizeDecimalMeasurementInput(event.target.value);
                          setModalLoadedInput(value);
                          setDraft((previous) => ({ ...previous, loadedLineBeforeTester: value }));
                        }}
                        onKeyDown={(event) =>
                          measurementInputKeyDownWithEnterFocusNext(
                            event,
                            modalLineHarvestBand === "gray"
                              ? inspectionModalLoadedWithTesterFieldRef.current
                              : lineHarvestNeedsOperatorChoice
                                ? inspectionModalLineHarvestChoiceGroupRef.current
                                : inspectionModalSubmitButtonRef.current,
                          )
                        }
                        className="mt-2 min-h-14 text-base tabular-nums"
                        inputMode="decimal"
                        autoComplete="off"
                        disabled={inspectionModalLineHarvestSectionDisabled}
                      />
                    </div>
                    {!inspectionModalLineHarvestSectionDisabled && modalDragLadder != null && modalLineHarvestBand === "gray" ? (
                      <div
                        className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-3 text-sm text-amber-950"
                        role="status"
                      >
                        <p className="font-semibold">Connect to a line tester</p>
                        <p className="mt-1 leading-snug">
                          Drag is in the marginal gray band. Connect this unit to a line tester, enter the loaded line
                          with the tester attached, and complete the tester run. Then choose{" "}
                          <span className="font-medium">Line harvest</span> Good or Failed to match the tester outcome.
                          Submitting records that this unit was run on a tester for this gray band.
                        </p>
                        <div className="mt-3">
                          <Label htmlFor="qc-modal-loaded-with-tester">Loaded line with tester</Label>
                          <Input
                            ref={inspectionModalLoadedWithTesterFieldRef}
                            id="qc-modal-loaded-with-tester"
                            value={modalLoadedWithTesterInput}
                            onChange={(event) =>
                              setModalLoadedWithTesterInput(sanitizeDecimalMeasurementInput(event.target.value))
                            }
                            onBlur={(event) => {
                              const value = sanitizeDecimalMeasurementInput(event.target.value);
                              setModalLoadedWithTesterInput(value);
                              setDraft((previous) => ({ ...previous, loadedLineWithTester: value }));
                            }}
                            onKeyDown={(event) =>
                              measurementInputKeyDownWithEnterFocusNext(
                                event,
                                lineHarvestNeedsOperatorChoice
                                  ? inspectionModalLineHarvestChoiceGroupRef.current
                                  : inspectionModalSubmitButtonRef.current,
                              )
                            }
                            className="mt-2 min-h-14 text-base tabular-nums"
                            inputMode="decimal"
                            autoComplete="off"
                            disabled={inspectionModalLineHarvestSectionDisabled}
                          />
                        </div>
                      </div>
                    ) : null}
                    {!inspectionModalLineHarvestSectionDisabled && lineHarvestNeedsOperatorChoice ? (
                      <div
                        ref={inspectionModalLineHarvestChoiceGroupRef}
                        data-clamp-qc-line-harvest-keypad
                        tabIndex={-1}
                        className="rounded-md outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
                        onKeyDown={(event) => {
                          if (event.nativeEvent.isComposing) {
                            return;
                          }
                          if (event.key === "1" || event.key === "Numpad1") {
                            event.preventDefault();
                            setDraft((previous) => ({ ...previous, lineHarvestFail: false }));
                          } else if (event.key === "2" || event.key === "Numpad2") {
                            event.preventDefault();
                            setDraft((previous) => ({ ...previous, lineHarvestFail: true }));
                          } else if (event.key === "Enter") {
                            event.preventDefault();
                            focusElementNextTick(inspectionModalSubmitButtonRef.current);
                          }
                        }}
                      >
                        <p className="text-sm font-medium text-slate-800">Line harvest</p>
                        <p className="mt-1 text-xs text-slate-500">
                          Numeric keypad: 1 Good, 2 Failed, then Enter moves focus to Record inspection.
                        </p>
                        <div className="mt-2 grid grid-cols-2 gap-3">
                          <Button
                            type="button"
                            variant={draft.lineHarvestFail === false ? "primary" : "secondary"}
                            className="min-h-14 text-base font-semibold"
                            onClick={() => setDraft((previous) => ({ ...previous, lineHarvestFail: false }))}
                          >
                            Good
                          </Button>
                          <Button
                            type="button"
                            variant={draft.lineHarvestFail === true ? "primary" : "secondary"}
                            className="min-h-14 text-base font-semibold"
                            onClick={() => setDraft((previous) => ({ ...previous, lineHarvestFail: true }))}
                          >
                            Failed
                          </Button>
                        </div>
                      </div>
                    ) : null}
                  </section>
                </div>
              </div>

              <div className="shrink-0 border-t border-slate-200 bg-slate-50 px-4 py-4 sm:px-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
                  <Button
                    type="button"
                    variant="secondary"
                    className="min-h-14 w-full text-base font-semibold sm:w-auto sm:min-w-[10rem]"
                    onClick={() => dismissInspectionDetailModal()}
                  >
                    Cancel
                  </Button>
                  <Button
                    ref={inspectionModalSubmitButtonRef}
                    type="submit"
                    disabled={inspectionSaving || inspectionModalFormIncompleteFlag}
                    variant={inspectionModalSubmitOutcomeResolved === "Failed" ? "danger" : "primary"}
                    className="min-h-14 w-full text-base font-semibold sm:w-auto sm:min-w-[14rem]"
                  >
                    {inspectionSaving
                      ? "Saving inspection…"
                      : inspectionModalSubmitOutcomeResolved === null
                      ? "Record inspection — complete required fields"
                      : `Record inspection — ${
                          inspectionModalSubmitOutcomeResolved === "Passed" ? "PASS" : "FAIL"
                        }`}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </>
  );
}
